/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonList,
  IonModal,
  IonPopover,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import {
  AWSAccessParams,
  AllProgressCheckStatuses,
  CPDActivityType,
  CompetenceActivityClass,
  EvidenceAttachment,
  EvidenceCustomTag,
  EvidencePart,
  EvidenceStandardTag,
  FieldType,
  IDateRange,
  IEvidence,
  IEvidenceModal,
  LearningObjective,
  INewDeepLinkEvidence,
  INewDeepLinkLearningLoop,
  INewGuidelineEvidence,
  INewTimelineEvidence,
  IUser,
  ProgramData,
  ProgramSkill,
  ProgressCheck,
  ProgressCheckApproval,
  EvidenceDefinition,
  WPBAForm,
} from "@interfaces/Interfaces";
import { useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";
import { evidenceAtom, progressCheckDataAtom, userAtom, userProgramsAtom } from "@state/State";
import ContentContainer from "./ContentContainer";
import "./CommonComponents.css";
import EvidenceContentCard from "./EvidenceContentCard";
import {
  Button_Dropdown,
  Chevron_Next_Active,
  Chevron_Next_Inactive,
  Chevron_Prev_Active,
  Chevron_Prev_Inactive,
  Icon_Attachment_Delete,
} from "@assets/images";
import { addDays, format, isAfter, isBefore, isSameDay, isToday, isValid } from "date-fns";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import _ from "lodash";
import { EventRegister } from "react-native-event-listeners";
import DataController from "@controllers/DataController";
import { useFilePicker } from "use-file-picker";
import * as DatabaseService from "@controllers/DatabaseService";
import AWSService from "@controllers/AWSService";
import * as FirebaseService from "@controllers/FirebaseService";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardInfo } from "@capacitor/keyboard";
import EvidenceHeader from "./EvidenceHeader";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import { updatePopoverTriggerWidth } from "@utils/popoverUtils";
import SaveDraftModal from "@components/evidence/SaveDraftModal";
import EmptyTagModal from "@components/evidence/EmptyTagModal";
import ExistingTagModal from "@components/evidence/ExistingTagModal";
import * as EditorUtils from "@utils/editorUtils";
import * as FileUtils from "@utils/fileUtils";
import * as ProgressCheckService from "@controllers/ProgressCheckService";
import DeleteEvidenceModal from "@components/evidence/DeleteEvidenceModal";
import AttachmentProgressIndicator from "@components/evidence/AttachmentProgressIndicator";
import UnsavedChangesModal from "@components/evidence/UnsavedChangesModal";
import { EVIDENCE_ACCEPTED_FILE_TYPES, EVIDENCE_MAX_FILE_SIZE } from "../../Constants";
import SaveEvidenceButton from "@components/evidence/SaveEvidenceButton";
import AttachmentInput from "@components/evidence/AttachmentInput";
import ProgramModuleEvidencePart from "@components/evidence/ProgramModuleEvidencePart";
import * as StringUtils from "@utils/stringUtils";
import { useRefreshProgressCheckData } from "@hooks/data/useRefreshProgressCheckData";
import { useProgramModuleStatus } from "@hooks/programs/useProgramModuleStatus";
import classNames from "classnames";
import DurationPicker from "./DurationPicker";
import CustomTagInput from "./CustomTagInput";
import EvidencePartWrapper from "./EvidencePartWrapper";
import { useEvidenceDictation } from "@hooks/ai/useEvidenceDictation";
import { useAIEvidencePreview } from "@hooks/ai/useAIEvidencePreview";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import WeekDayRenderer from "@components/programs/evidenceParts/WeekdayRenderer";
import UOCSubmitConfirmation from "@components/programs/UOCSubmitConfirmation";
import PersonalEvidenceDisclaimer from "@components/evidence/PersonalEvidenceDisclaimer";
import Textarea from "@mui/joy/Textarea";
import HeaderTitle from "./HeaderTitle";
import { userRolesSelector } from "@state/UserState";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import ReuseIncidentModal from "@components/evidence/ReuseIncidentModal";
import { usePreviousIncidentDetails } from "@hooks/programs/usePreviousIncidentDetails";
import { useRefreshUserEvidence } from "@hooks/data/useRefreshUserEvidence";
import * as SPAPRPUtils from "@utils/programs/SPAPRPUtils";
import AIAssistButton from "@components/aiAssist/AIAssistButton";
import AIAssistModal from "@components/aiAssist/AIAssistModal";
import AIAssistFeedback from "@components/aiAssist/AIAssistFeedback";
import { AIMessage } from "@interfaces/AIAssistTypes";

const defaultClassNames = getDefaultClassNames();

const EvidenceModal = ({ visible, closeModal }: IEvidenceModal) => {
  const [openFileSelector, { filesContent, clear, plainFiles }] = useFilePicker({
    accept: EVIDENCE_ACCEPTED_FILE_TYPES,
    maxFileSize: EVIDENCE_MAX_FILE_SIZE,
    readAs: "DataURL",
    multiple: true,
  });

  const allUserRoles = useRecoilValue(userRolesSelector);
  const { refreshProgressCheckData } = useRefreshProgressCheckData();
  const { refreshUserEvidence } = useRefreshUserEvidence();
  const {
    addPreviousIncidentDetailsToEvidence,
    createPreviousIncidentDetailsObject,
    updateIncidentDetails,
    shouldShowReuseIncidentModal,
  } = usePreviousIncidentDetails();

  const titleRef = useRef<HTMLInputElement>(null);
  const activityDescriptionRef = useRef<HTMLTextAreaElement>(null);
  const reflectionsEditorRef = useRef<TinyMCEEditor | null>(null);
  const benefitsEditorRef = useRef<TinyMCEEditor | null>(null);
  const objectivesEditorRef = useRef<TinyMCEEditor | null>(null);
  const dictationBannerRef = useRef<HTMLDivElement>(null);

  const activityTypes: CPDActivityType[] = EvidenceUtils.getDefaultActivityTypes();

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const userPrograms = useRecoilValue<ProgramData[] | any[]>(userProgramsAtom);
  const _e = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const setEvidenceAtom = useSetRecoilState(evidenceAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[]>(progressCheckDataAtom);

  const evidenceRef = useRef<IEvidence[]>(_e.contents ?? []);
  const allProgressCheckDataRef = useRef<AllProgressCheckStatuses[]>(allProgressCheckData.contents ?? []);

  const [initialEvidence, setInitialEvidence] = useState<IEvidence | null>(null);
  const [timelineReference, setTimelineReference] = useState<string>("");

  const [isNewEvidence, setIsNewEvidence] = useState<boolean>(true);
  const [isDraft, setIsDraft] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(visible);

  // Personal evidence inputs
  const [title, setTitle] = useState<string>("");
  const [date, setDate] = useState<Date>(new Date());
  const [activity, setActivity] = useState<CPDActivityType | null>(null);
  const [numberOfHours, setNumberOfHours] = useState<string>("");
  const [customTags, setCustomTags] = useState<EvidenceCustomTag[]>([]);
  const [attachments, setAttachments] = useState<EvidenceAttachment[]>([]);
  const [activityDescription, setActivityDescription] = useState<string>("");
  const [reflections, setReflection] = useState<string>("");
  const [benefits, setBenefits] = useState<string>("");
  const [objectives, setObjectives] = useState<string>("");
  const [addToHCPCAudit, setAddToHCPCAudit] = useState<boolean>(false);
  const [dictationInput, setDictationInput] = useState<string>("");
  const [id, setId] = useState<string>("");
  // Activity Date range
  const [activityDateMode, setActivityDateMode] = useState<"single" | "range">("single");
  const [activitySingleDate, setActivitySingleDate] = useState<Date>(new Date());
  const [activityDateRange, setActivityDateRange] = useState<IDateRange | undefined>(undefined);

  // SWAST MIC inputs
  const [standardTags, setStandardTags] = useState<EvidenceStandardTag[]>([]);

  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);
  const [activityDatePickerVisible, setActivityDatePickerVisible] = useState<boolean>(false);

  const [attachmentsToAdd, setAttachmentsToAdd] = useState<EvidenceAttachment[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<EvidenceAttachment[]>([]);
  const [handlingAttachments, setHandlingAttachments] = useState<boolean>(false);
  const [viewingAttachment, setViewingAttachment] = useState<boolean>(false);
  const [viewingAttachmentFilename, setViewingAttachmentFilename] = useState<string>("");

  const [reflectionsFocused, setReflectionsFocused] = useState<boolean>(false);
  const [benefitsFocused, setBenefitsFocused] = useState<boolean>(false);
  const [objectivesFocused, setObjectivesFocused] = useState<boolean>(false);
  const [richTextBoxFocused, setRichTextBoxFocused] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);

  const [canSaveDraft, setCanSaveDraft] = useState<boolean>(false);
  const [saveDraftModalVisible, setSaveDraftModalVisible] = useState<boolean>(false);
  const [deleteEvidenceModalVisible, setDeleteEvidenceModalVisible] = useState<boolean>(false);
  const [emptyTagModalVisible, setEmptyTagModalVisible] = useState<boolean>(false);
  const [existingTagModalVisible, setExistingTagModalVisible] = useState<boolean>(false);
  const [unsavedChangesModalVisible, setUnsavedChangesModalVisible] = useState<boolean>(false);
  const [reuseIncidentModalVisible, setReuseIncidentModalVisible] = useState<boolean>(false);
  const [aiAssistModalVisible, setAiAssistModalVisible] = useState<boolean>(false);
  const [aiAssistFeedbackVisible, setAiAssistFeedbackVisible] = useState<boolean>(false);
  const [aiAssistSessionId, setAiAssistSessionId] = useState<string | null>(null);
  const [aiAssistMessages, setAiAssistMessages] = useState<AIMessage[]>([]);
  const [isAIAssisted, setIsAIAssisted] = useState<boolean>(false);

  const [keyboardActive, setKeyboardActive] = useState<boolean>(false);
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  // Program evidence
  const [program, setProgram] = useState<ProgramData | null>(null);
  const [competence, setCompetence] = useState<CompetenceActivityClass | null>(null);
  const [competences, setCompetences] = useState<CompetenceActivityClass[] | any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [enhancedSkill, setEnhancedSkill] = useState<ProgramSkill | null>(null);
  const [evidenceParts, setEvidenceParts] = useState<EvidencePart[] | any[]>([]);
  const [progressCheck, setProgressCheck] = useState<ProgressCheck | null>(null);
  const [progressChecks, setProgressChecks] = useState<ProgressCheck[] | any[]>([]);
  const [progressCheckCompetences, setProgressCheckCompetences] = useState<CompetenceActivityClass[] | any[]>([]);
  const [progressCheckCompetence, setProgressCheckCompetence] = useState<CompetenceActivityClass | null>(null);
  const [programMandatoryStandards, setProgramMandatoryStandards] = useState<CompetenceActivityClass[]>([]);
  const [programOptionalStandards, setProgramOptionalStandards] = useState<CompetenceActivityClass[]>([]);
  const [programSkills, setProgramSkills] = useState<ProgramSkill[]>([]);
  const [preselectedSkill, setPreselectedSkill] = useState<ProgramSkill | null>(null);
  const [preselectedLearningObjective, setPreselectedLearningObjective] = useState<LearningObjective | null>(null);
  const [wpbaForm, setWpbaForm] = useState<WPBAForm | null>(null);
  const [expandStandardId, setExpandStandardId] = useState<string | null>(null);

  const [programEvidenceDefinition, setProgramEvidenceDefinition] = useState<EvidenceDefinition | null>(null);
  const [programLearningObjectives, setProgramLearningObjectives] = useState<LearningObjective[]>([]);
  const [approvalState, setApprovalState] = useState<string | null>(null);
  const [autoFilledSkill, setAutoFilledSkill] = useState<ProgramSkill | null>(null);

  const [evidenceJSON, setEvidenceJSON] = useState<any>({});
  const [initialEvidenceJSON, setInitialEvidenceJSON] = useState<any>({});

  // Disabling dropdowns for Progress Checks
  const [disableProgressCheckDropdown, setDisableProgressCheckDropdown] = useState<boolean>(false);
  const [disableProgressCheckCompetenceDropdown, setDisableProgressCheckCompetenceDropdown] = useState<boolean>(false);

  // Confirmation modal for changing enhanced skill
  const [changeTaskingTypeModalVisible, setChangeTaskingTypeModalVisible] = useState<boolean>(false);
  const [tempTaskingType, setTempTaskingType] = useState<string>("");

  // Confirmation modal for changing progress check type
  const [changeProgressCheckModal, setChangeProgressCheckModalVisible] = useState<boolean>(false);
  const [tempProgressCheck, setTempProgressCheck] = useState<string>("");

  // Confirmation modal for changing progress check competence
  const [changeProgressCheckCompetenceModal, setChangeProgressCheckCompetenceModalVisible] = useState<boolean>(false);
  const [tempProgressCheckCompetence, setTempProgressCheckCompetence] = useState<string>("");

  const [tempInput, setTempInput] = useState<string>("");
  const [confirmationEvidence, setConfirmationEvidence] = useState<IEvidence | null>(null);

  // Mandatory field handling
  const errorBannerRef = useRef<HTMLDivElement>(null);
  const [showErrorBanner, setShowErrorBanner] = useState<boolean>(false);
  const mandatoryFieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [incompleteMandatoryFields, setIncompleteMandatoryFields] = useState<string[]>([]);
  const [activeMandatoryFieldIndex, setActiveMandatoryFieldIndex] = useState(0);
  const disableNextChevron = incompleteMandatoryFields.length === activeMandatoryFieldIndex + 1;
  const disablePrevChevron = activeMandatoryFieldIndex === 0;

  const { latestEvidencePreview, setLatestEvidencePreview, resetLatestEvidencePreview } = useAIEvidencePreview({
    title,
    date,
    activity,
    numberOfHours,
    activityDescription,
    reflections,
    benefits,
    objectives,
    customTags,
    activityDateMode,
    activitySingleDate,
    activityDateRange,
    addToHCPCAudit,
  });

  // Reset latestEvidencePreview when modal closes
  useEffect(() => {
    if (!visible) {
      resetLatestEvidencePreview();
    }
  }, [visible, resetLatestEvidencePreview]);

  const { currentProgramCycle } = useProgramCycles(program?.ID ?? "");
  const { fastTrackActive, pauseAndHold } = useProgramModuleStatus(program?.ID);

  const evidenceToPass = useMemo(() => {
    return {
      ...(title.length > 0 && { title }),
      ...(date && { date: date.toISOString() }),
      ...(activity && { activity }),
      ...(numberOfHours.length > 0 && { numberOfHours: DataController.convertToNumberOfHours(numberOfHours) }),
      ...(standardTags.length > 0 && { standardTags }),
      ...(customTags.length > 0 && { customTags }),
      ...(activityDescription.length > 0 && { activityDescription }),
      ...(reflections.length > 0 && { reflections }),
      ...(benefits.length > 0 && { benefits }),
      ...(objectives.length > 0 && { objectives }),
      ...(addToHCPCAudit && { addToHCPCAudit }),
      ...(activitySingleDate && { activityDate: activitySingleDate.toISOString() }),
      id,
    };
  }, [
    title,
    date,
    activity,
    numberOfHours,
    standardTags,
    customTags,
    activityDescription,
    reflections,
    benefits,
    objectives,
    addToHCPCAudit,
    activitySingleDate,
    id,
  ]);

  const {
    state: dictationState,
    processingState,
    resetProcessingState,
    isAvailable: isAIDictationAvailable,
    processedEvidence,
    transcriptHistory,
    resetDictation,
    changeActivityState,
  } = useEvidenceDictation(evidenceToPass);

  useEffect(() => {
    evidenceRef.current = _e.contents;
  }, [_e.contents]);

  useEffect(() => {
    try {
      benefits.length > 0 && benefitsEditorRef.current?.getBody()?.setAttribute("data-mce-placeholder", "");
      objectives.length > 0 && objectivesEditorRef.current?.getBody()?.setAttribute("data-mce-placeholder", "");
      reflections.length > 0 && reflectionsEditorRef.current?.getBody()?.setAttribute("data-mce-placeholder", "");
    } catch (error) {
      console.log(error);
    }
  }, [benefits, objectives, reflections]);

  useEffect(() => {
    allProgressCheckDataRef.current = allProgressCheckData.contents;
  }, [allProgressCheckData.contents]);

  useEffect(() => {
    if (Capacitor.getPlatform() === "ios") {
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        setKeyboardActive(true);
        setKeyboardHeight(info.keyboardHeight);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardActive(false);
      });
    }

    return () => {
      if (Capacitor.getPlatform() === "ios") {
        Keyboard.removeAllListeners();
      }
    };
  }, []);

  useEffect(() => {
    if (progressCheck) {
      getProgressCheckCompetences(progressCheck);
    }
  }, [progressCheck]);

  useEffect(() => {
    if (plainFiles.length > 0 && filesContent.length > 0) {
      const result = EvidenceUtils.addAttachmentToUploadArray(plainFiles, filesContent, attachments, attachmentsToAdd);

      setAttachments(result.attachments);
      setAttachmentsToAdd(_.uniqBy(result.toAdd, "name"));
      clear();
    }
  }, [attachments, attachmentsToAdd, attachmentsToDelete, clear, filesContent, plainFiles]);

  useEffect(() => {
    const checkAttachments = () => {
      if (attachments.length > 0) {
        const toAdd = _.cloneDeep(attachmentsToAdd);

        for (let i = 0; i < attachments.length; i++) {
          const attachment = attachments[i];
          const includeAdd = toAdd.findIndex((item) => item.name === attachment.name);
          const includeDelete = attachmentsToDelete.findIndex((item) => item.name === attachment.name);

          if (includeAdd === -1 && includeDelete === -1 && attachment.content) {
            toAdd.push(attachment);
          }
        }

        setAttachmentsToAdd(_.uniqBy(toAdd, "name"));
      }
    };

    const checkAttachmentsNeedUploading = () => {
      let _attachments = _.cloneDeep(attachments);
      _attachments = _attachments.filter((item) => item.content);
      return _attachments.length > 0;
    };

    if (attachmentsToAdd.length === 0 && checkAttachmentsNeedUploading()) {
      checkAttachments();
    }
  }, [attachments, attachmentsToAdd, attachmentsToDelete]);

  useEffect(() => {
    const checkAttachments = () => {
      if (attachments.length > 0) {
        const toAdd = _.cloneDeep(attachmentsToAdd);

        for (let i = 0; i < attachments.length; i++) {
          const attachment = attachments[i];
          const includeAdd = toAdd.findIndex((item) => item.name === attachment.name);
          const includeDelete = attachmentsToDelete.findIndex((item) => item.name === attachment.name);

          if (includeAdd === -1 && includeDelete === -1 && attachment.content) {
            toAdd.push(attachment);
          }
        }

        setAttachmentsToAdd(_.uniqBy(toAdd, "name"));
      }
    };

    const checkAttachmentsNeedUploading = () => {
      let _attachments = _.cloneDeep(attachments);
      _attachments = _attachments.filter((item) => item.content);
      return _attachments.length > 0;
    };

    if (attachmentsToAdd.length === 0 && checkAttachmentsNeedUploading()) {
      checkAttachments();
    }
  }, [attachments, attachmentsToAdd, attachmentsToDelete]);

  const getProgressCheckCompetences = useCallback(
    (_progressCheck: ProgressCheck): void => {
      if (program) {
        let _competences = _.cloneDeep(_progressCheck["Competence/Activity"]);
        _competences = ProgramUtils.removeProgressCheckCompetences(
          _competences,
          _progressCheck,
          evidenceRef.current,
          program.ID,
          progressCheckCompetence,
          fastTrackActive
        );

        setProgressCheckCompetences(_competences);
      }
    },
    [program?.ID, fastTrackActive]
  );

  const getIncompleteProgressChecks = useCallback(
    (_progressChecks: ProgressCheck[], _program: ProgramData, _progressCheck?: ProgressCheck | null): void => {
      let array = _.cloneDeep(_progressChecks);
      array = ProgramUtils.removeProgressChecks(
        array,
        evidenceRef.current,
        _program,
        allProgressCheckDataRef.current,
        fastTrackActive,
        pauseAndHold,
        _progressCheck
      );

      setProgressChecks(array);
    },
    [fastTrackActive, pauseAndHold]
  );

  useEffect(() => {
    EventRegister.addEventListener("evidence-modal/add-evidence-guideline", (object: INewGuidelineEvidence) => {
      setTitle(object.title || "");
    });

    EventRegister.addEventListener("evidence-modal/add-evidence-timeline", (object: INewTimelineEvidence) => {
      setTitle(object.title);
      setDate(object.date);
      setActivitySingleDate(object.activityDate);
      setActivityDateMode("single");
      setActivityDescription(object.activityDescription);
      setTimelineReference(object.timelineReference);

      if (object.pdfData && object.pdfData.pdf) {
        const _attachment = {
          name: object.pdfData.certificateFileName,
          type: "application/pdf",
          size: object.pdfData.size,
          content: object.pdfData.pdf,
        };

        setAttachments([_attachment]);
        setAttachmentsToAdd([_attachment]);
      }
    });

    EventRegister.addEventListener("evidence-modal/add-evidence-deeplink", (object: INewDeepLinkEvidence) => {
      setTitle(object.title ?? "");
      setDate(object.date ? new Date(object.date) : new Date());
      setActivitySingleDate(object.activityDate ? new Date(object.activityDate) : new Date());
      setActivityDateMode("single");
      setActivityDescription(object.activityDescription ?? "");
      setTimelineReference(object.timelineReference ?? "");
      setNumberOfHours(object.activityDuration ? DataController.convertToHoursMinutes(object.activityDuration) : "");
      setActivity(object.activityType ? { type: object.activityType } : null);
      setReflection(object.reflection ?? "");
      setBenefits(object.benefits ?? "");
      setObjectives(object.learningObjectives ?? "");

      if (object.pdfData && object.pdfData.pdf) {
        const _attachment = {
          name: object.pdfData.certificateFileName,
          type: "application/pdf",
          size: object.pdfData.size,
          content: object.pdfData.pdf,
        };

        setAttachments([_attachment]);
        setAttachmentsToAdd([_attachment]);
      }
    });

    EventRegister.addEventListener("evidence-modal/add-learning-loop-deeplink", (object: INewDeepLinkLearningLoop) => {
      if (object.programID) {
        const program = userPrograms.find((program) => program.ID === object.programID);
        const progressCheck = program?.ProgressChecks.find(
          (progressCheck: ProgressCheck) => progressCheck.ID === object.progressCheckID
        );
        const progressCheckCompetence = progressCheck?.["Competence/Activity"].find(
          (competence: CompetenceActivityClass) => competence.ID === object.progressCheckCompetenceID
        );

        setProgram(program);
        setProgressCheck(progressCheck);
        setProgressChecks(program?.ProgressChecks);
        setProgressCheckCompetence(progressCheckCompetence);
        setDisableProgressCheckDropdown(true);
        setDisableProgressCheckCompetenceDropdown(true);
        const evidenceParts = getEvidenceParts(progressCheckCompetence);

        addDeepLinkEvidenceParts(object, evidenceParts);
      }
    });

    EventRegister.addEventListener("timeline/edit-evidence-clicked", (object: IEvidence) => {
      setTitle(object.title || "");
      setDate(new Date(object.date));
      setActivity(!_.isEmpty(object.activity) ? object.activity : null);
      setNumberOfHours((object.numberOfHours && DataController.convertToHoursMinutes(object.numberOfHours)) || "");
      object.standardTags && setStandardTags(object.standardTags);
      object.customTags && setCustomTags(object.customTags);
      object.attachments && setAttachments(object.attachments);
      setActivityDescription(object.activityDescription || "");
      setReflection(object.reflections || "");
      setBenefits(object.benefits || "");
      setObjectives(object.objectives || "");
      setDictationInput(object.dictationInput ?? "");
      setId(object.id);
      object.addToHCPCAudit !== undefined && setAddToHCPCAudit(object.addToHCPCAudit);
      object.timelineReference && setTimelineReference(object.timelineReference);
      setIsDraft(object.draft === true);
      setIsAIAssisted(object.aiAssisted ?? false);
      if (object.activityDate && object.activityDateEnd) {
        setActivityDateRange({ from: new Date(object.activityDate), to: new Date(object.activityDateEnd) });
        setActivityDateMode("range");
      } else if (object.activityDate) {
        setActivitySingleDate(new Date(object.activityDate));
        setActivityDateMode("single");
      }

      setInitialEvidence(object);
      setIsNewEvidence(false);

      // Store the latest evidence preview for future AI Assist sessions
      setLatestEvidencePreview(object);
    });

    EventRegister.addEventListener("evidence/evidence-card-clicked", (object: IEvidence) => {
      setTitle(object.title || "");
      setDate(new Date(object.date));
      setActivity(!_.isEmpty(object.activity) ? object.activity : null);
      setNumberOfHours((object.numberOfHours && DataController.convertToHoursMinutes(object.numberOfHours)) || "");
      object.standardTags && setStandardTags(object.standardTags);
      object.customTags && setCustomTags(object.customTags);
      object.attachments && setAttachments(object.attachments);
      setActivityDescription(object.activityDescription || "");
      setReflection(object.reflections || "");
      setBenefits(object.benefits || "");
      setObjectives(object.objectives || "");
      setDictationInput(object.dictationInput ?? "");
      setId(object.id);
      object.addToHCPCAudit !== undefined && setAddToHCPCAudit(object.addToHCPCAudit);
      object.timelineReference && setTimelineReference(object.timelineReference);
      setIsAIAssisted(object.aiAssisted ?? false);
      if (object.activityDate && object.activityDateEnd) {
        setActivityDateRange({ from: new Date(object.activityDate), to: new Date(object.activityDateEnd) });
        setActivityDateMode("range");
      } else if (object.activityDate) {
        setActivitySingleDate(new Date(object.activityDate));
        setActivityDateMode("single");
      }

      setInitialEvidence(object);
      setIsDraft(object.draft === true);
      setIsNewEvidence(false);

      // Store the latest evidence preview for future AI Assist sessions
      setLatestEvidencePreview(object);
    });

    EventRegister.addEventListener("evidence/add-program-evidence", (data) => {
      data.program && setProgram(data.program);
      data.skill && setEnhancedSkill(data.skill);
      data.competences && setCompetences(data.competences);
      data.progressCompetences && setProgressCheckCompetences(data.progressCompetences);

      if (data.evidenceJSON) {
        setDate(DataController.getDefaultDateFromProgramEvidence(data.evidenceJSON));
        setEvidenceJSON(data.evidenceJSON);
        setInitialEvidenceJSON(data.evidenceJSON);
        setIsNewEvidence(false);
        setIsDraft(data?.evidence?.draft === true);
        setNumberOfHours(
          (data?.evidence?.numberOfHours && DataController.convertToHoursMinutes(data?.evidence?.numberOfHours)) || ""
        );
        data?.evidence?.addToHCPCAudit !== undefined && setAddToHCPCAudit(data.evidence.addToHCPCAudit);
        setIsAIAssisted(data?.evidence?.aiAssisted ?? false);
        setId(data.id);
      }

      if (data.competence) {
        setCompetence(data.competence);
        getEvidenceParts(data.competence);
      }

      if (data.progressCheck && data.progressCheck["Competence/Activity"]) {
        setProgressCheck(data.progressCheck);
        getProgressCheckCompetences(data.progressCheck);
      }

      if (ProgramUtils.isCSM(data.program.ID) && data.progressChecks) {
        setProgressChecks(data.progressChecks);
      } else if (data.progressChecks) {
        getIncompleteProgressChecks(data.progressChecks, data.program, data.progressCheck);
      }

      if (data.progressCompetence) {
        setProgressCheckCompetence(data.progressCompetence);
        getEvidenceParts(data.progressCompetence);
      }

      if (data.mandatoryStandards) {
        setProgramMandatoryStandards(data.mandatoryStandards);
      }

      if (data.optionalStandards) {
        setProgramOptionalStandards(data.optionalStandards);
      }

      if (data.skills) {
        setProgramSkills(data.skills);
      }

      if (data.preselectedSkill) {
        setPreselectedSkill(data.preselectedSkill);
      }

      if (data.preselectedLearningObjective) {
        setPreselectedLearningObjective(data.preselectedLearningObjective);
      }

      if (data.evidenceDefinitions) {
        setProgramEvidenceDefinition(data.evidenceDefinitions);
        setEvidenceParts(data.evidenceDefinitions?.["Evidence parts"]);
      }

      if (data.preselectedSkill && data.evidenceDefinitions) {
        const triggerValues = EvidenceUtils.getTriggerValuesForSkill(
          data.evidenceDefinitions["Evidence parts"] ?? [],
          data.preselectedSkill,
          data.skills ?? []
        );

        if (!_.isEmpty(triggerValues)) {
          setEvidenceJSON((current: Record<string, unknown>) => ({ ...current, ...triggerValues }));
          setInitialEvidenceJSON((current: Record<string, unknown>) => ({ ...current, ...triggerValues }));
        }
      }

      if (data.approvalState) {
        setApprovalState(data.approvalState);
      }

      if (data.autoFilledSkill) {
        setAutoFilledSkill(data.autoFilledSkill);
      }

      if (data.expandStandardId) {
        setExpandStandardId(data.expandStandardId);
      }

      if (data.selectedStandard) {
        const newEvidenceObject = EvidenceUtils.addSelectedStandard(data.evidenceJSON ?? {}, data.selectedStandard);
        setEvidenceJSON(newEvidenceObject);
      }

      if (data.disableProgressCheckDropdown) {
        setDisableProgressCheckDropdown(data.disableProgressCheckDropdown);
      }

      if (data.disableProgressCheckCompetenceDropdown) {
        setDisableProgressCheckCompetenceDropdown(data.disableProgressCheckCompetenceDropdown);
      }

      if (data.learningObjectives) {
        setProgramLearningObjectives(data.learningObjectives);
      }

      if (data.preselectedTitle) {
        setTitle(data.preselectedTitle);
        setEvidenceJSON((current: Record<string, unknown>) => ({ ...current, Title: data.preselectedTitle }));
        setInitialEvidenceJSON((current: Record<string, unknown>) => ({ ...current, Title: data.preselectedTitle }));
      }

      if (data.preselectedDuration) {
        setNumberOfHours(data.preselectedDuration);
      }

      if (data.timelineReference) {
        setTimelineReference(data.timelineReference);
      }

      if (data.pdfData && data.pdfData.pdf) {
        const _attachment = {
          name: data.pdfData.certificateFileName,
          type: "application/pdf",
          size: data.pdfData.size,
          content: data.pdfData.pdf,
        };

        setAttachments([_attachment]);
        setAttachmentsToAdd([_attachment]);
      }

      if (data.prefilledNewEvidence) {
        setIsNewEvidence(true);
        setIsDraft(false);
        setId("");
        setCanSaveDraft(true);
      }

      if (data.wpbaForm) {
        setWpbaForm(data.wpbaForm);
      }
    });

    EventRegister.addEventListener("evidence/add-progress-check-evidence", (data) => {
      data.program && setProgram(data.program);
      data.progressCheck && setProgressCheck(data.progressCheck);
      data.progressChecks && setProgressChecks(data.progressChecks);
      data.progressCompetences && setProgressCheckCompetences(data.progressCompetences);

      if (data.progressChecks && !ProgramUtils.isCSM(data.program.ID)) {
        getIncompleteProgressChecks(data.progressChecks, data.program, data.progressCheck);
      }

      if (data.progressCompetence) {
        setProgressCheckCompetence(data.progressCompetence);
        getEvidenceParts(data.progressCompetence);
        getProgressCheckCompetences(data.progressCheck);
      } else if (data.progressCheck && data.progressCheck["Competence/Activity"]) {
        getProgressCheckCompetences(data.progressCheck);
      }

      if (data.skills) {
        setProgramSkills(data.skills);
      }

      if (data.preselectedSkill) {
        setPreselectedSkill(data.preselectedSkill);
      }

      if (data.preselectedLearningObjective) {
        setPreselectedLearningObjective(data.preselectedLearningObjective);
      }

      if (data.disableProgressCheckDropdown) {
        setDisableProgressCheckDropdown(data.disableProgressCheckDropdown);
      }

      if (data.disableProgressCheckCompetenceDropdown) {
        setDisableProgressCheckCompetenceDropdown(data.disableProgressCheckCompetenceDropdown);
      }
    });

    return () => {
      EventRegister.removeEventListener("evidence-modal/add-evidence-guideline");
      EventRegister.removeEventListener("evidence-modal/add-evidence-timeline");
      EventRegister.removeEventListener("evidence-modal/add-evidence-deeplink");
      EventRegister.removeEventListener("evidence/evidence-card-clicked");
      EventRegister.removeEventListener("evidence/add-program-evidence");
      EventRegister.removeEventListener("evidence/add-progress-check-evidence");
      EventRegister.removeEventListener("timeline/edit-evidence-clicked");
    };
  }, []);

  useEffect(() => {
    EventRegister.addEventListener("evidence/add-skill-evidence", (data) => {
      data.program && setProgram(data.program);
      data.skill && setEnhancedSkill(data.skill);
      data.competences && setCompetences(data.competences);

      if (data.competence) {
        setCompetence(data.competence);
        getEvidenceParts(data.competence);

        const newEvidenceObject = EvidenceUtils.addRequiredSkill({}, data.skill, data.competence);
        setEvidenceJSON(newEvidenceObject);
      }
    });

    return () => {
      EventRegister.removeEventListener("evidence/add-skill-evidence");
    };
  }, []);

  useEffect(() => {
    setModalVisible(visible);
  }, [visible]);

  const processEvidencePreviewSaved = (params: {
    evidence: IEvidence | null;
    sessionId: string;
    messages: AIMessage[];
  }) => {
    const { evidence: evidenceToProcess, sessionId, messages } = params;

    if (evidenceToProcess) {
      evidenceToProcess.title && setTitle(evidenceToProcess.title);
      evidenceToProcess.date && setDate(new Date(evidenceToProcess.date));
      evidenceToProcess.activity && setActivity(evidenceToProcess.activity);
      evidenceToProcess.numberOfHours &&
        setNumberOfHours(DataController.convertToHoursMinutes(evidenceToProcess.numberOfHours));

      evidenceToProcess.activityDescription && setActivityDescription(evidenceToProcess.activityDescription);
      evidenceToProcess.reflections && setReflection(evidenceToProcess.reflections);
      evidenceToProcess.benefits && setBenefits(evidenceToProcess.benefits);
      evidenceToProcess.objectives && setObjectives(evidenceToProcess.objectives);
      evidenceToProcess.addToHCPCAudit != null && setAddToHCPCAudit(evidenceToProcess.addToHCPCAudit);

      if (evidenceToProcess.activityDate && evidenceToProcess.activityDateEnd) {
        setActivityDateRange({
          from: new Date(evidenceToProcess.activityDate),
          to: new Date(evidenceToProcess.activityDateEnd),
        });
        setActivityDateMode("range");
      } else if (evidenceToProcess.activityDate) {
        setActivitySingleDate(new Date(evidenceToProcess.activityDate));
        setActivityDateMode("single");
      }

      if (evidenceToProcess.customTags) {
        const array = _.uniqBy([...evidenceToProcess.customTags, ...customTags], "tag");
        setCustomTags(array);
      }

      if (dictationInput.length > 0) {
        setDictationInput((prev) => `${prev};${transcriptHistory.join(";")}`);
      } else {
        setDictationInput(transcriptHistory.join(";"));
      }

      setAiAssistSessionId(sessionId);
      setAiAssistMessages(messages);
      setAiAssistFeedbackVisible(true);
      setIsAIAssisted(true);
      isNewEvidence && setCanSaveDraft(true);

      // Store the latest evidence preview for future AI Assist sessions
      setLatestEvidencePreview(evidenceToProcess);
    }
  };

  useEffect(() => {
    if (processingState === "processed" && processedEvidence) {
      processedEvidence.title && setTitle(processedEvidence.title);
      processedEvidence.date && setDate(new Date(processedEvidence.date));
      processedEvidence.activity && setActivity(processedEvidence.activity);
      processedEvidence.numberOfHours &&
        setNumberOfHours(DataController.convertToHoursMinutes(processedEvidence.numberOfHours));

      processedEvidence.activityDescription && setActivityDescription(processedEvidence.activityDescription);
      processedEvidence.reflections && setReflection(processedEvidence.reflections);
      processedEvidence.benefits && setBenefits(processedEvidence.benefits);
      processedEvidence.objectives && setObjectives(processedEvidence.objectives);
      processedEvidence.addToHCPCAudit != null && setAddToHCPCAudit(processedEvidence.addToHCPCAudit);

      if (processedEvidence.activityDate && processedEvidence.activityDateEnd) {
        setActivityDateRange({
          from: new Date(processedEvidence.activityDate),
          to: new Date(processedEvidence.activityDateEnd),
        });
        setActivityDateMode("range");
      } else if (processedEvidence.activityDate) {
        setActivitySingleDate(new Date(processedEvidence.activityDate));
        setActivityDateMode("single");
      }

      if (processedEvidence.customTags) {
        const array = _.uniqBy([...processedEvidence.customTags, ...customTags], "tag");
        setCustomTags(array);
      }

      if (dictationInput.length > 0) {
        setDictationInput((prev) => `${prev};${transcriptHistory.join(";")}`);
      } else {
        setDictationInput(transcriptHistory.join(";"));
      }

      isNewEvidence && setCanSaveDraft(true);
      resetProcessingState();
    }
  }, [dictationState, processedEvidence, transcriptHistory]);

  const deleteEvidence = async (): Promise<void> => {
    if (user.state === "hasValue" && user.contents) {
      try {
        if (id) {
          if (attachments.length > 0) {
            await deleteAttachments(attachments, id);
          }

          const deleteFromDB = await DatabaseService.deleteEvidence(user.contents, id);

          try {
            if (evidenceJSON.programInfo && evidenceJSON.programInfo.progressCheckID) {
              const result = await ProgressCheckService.markProgressCheckAsInProgress(
                user.contents,
                program,
                evidenceJSON.programInfo.progressCheckID
              );

              result && (await refreshProgressCheckData(user.contents));
            }
          } catch (error) {
            console.error(error);
          }

          if (deleteFromDB) {
            // Remove from atom immediately so the list is already updated when the modal closes
            setEvidenceAtom((prev: IEvidence[] | null) => prev?.filter((e: IEvidence) => e.id !== id) ?? prev);

            EventRegister.emit("evidence/evidence-deleted");
            dismissModal();

            // Refresh in background to confirm server state
            refreshUserEvidence(user.contents).catch(console.error);
          }
        }
      } catch (error) {
        console.error(error);
        window.alert("An error occurred when deleting evidence");
      }
    }
  };

  const scrollToMandatoryField = (index: number, incompleteFields: string[]) => {
    // scroll to div
    const key = incompleteFields[index];
    const mandatoryField = mandatoryFieldRefs.current[key];
    if (mandatoryField) {
      // TODO: Add height of AI banner to calculation
      mandatoryField.style.scrollMargin =
        16 + (errorBannerRef.current?.clientHeight || 0) + (dictationBannerRef.current?.clientHeight || 0) + "px";
      mandatoryField.scrollIntoView({ behavior: "smooth" });
    }

    // animate div border
    const firstDivChild = mandatoryField?.querySelector("div:first-child");
    firstDivChild?.classList.add("border-pulse-animation");
    setTimeout(() => {
      firstDivChild?.classList.remove("border-pulse-animation");
    }, 1500); // Duration of the animation
  };

  const uploadAttachments = async (_attachments: EvidenceAttachment[], id: string): Promise<EvidenceAttachment[]> => {
    const toUpload = _.cloneDeep(_attachments);
    const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user.contents);

    const uploaded = [];

    for (let i = 0; i < toUpload.length; i++) {
      let file = await AWSService.uploadAttachment(user.contents, accessParams, id, toUpload[i]);

      if (file && file.networkIssue) {
        file = await AWSService.uploadAttachmentFromServer(user.contents, accessParams, id, toUpload[i]);
      }

      const updatedFile: EvidenceAttachment = {
        ...toUpload[i],
        content: undefined,
        url: file.Location,
      };

      delete updatedFile.content;

      uploaded.push(updatedFile);
    }

    return uploaded;
  };

  const deleteAttachments = async (_attachments: EvidenceAttachment[], id: string): Promise<string[]> => {
    const toDelete = _.cloneDeep(_attachments);
    const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user.contents);

    const deleted = [];

    for (let i = 0; i < toDelete.length; i++) {
      const fileDeleted = await AWSService.deleteAttachment(user.contents, accessParams, id, toDelete[i]);

      if (fileDeleted) {
        deleted.push(toDelete[i].name);
      }
    }

    return deleted;
  };

  const saveEvidence = async (isDraftEvidence?: boolean): Promise<void> => {
    try {
      isDraftEvidence ? setSavingDraft(true) : setSaving(true);

      if (user.state === "hasValue" && user.contents) {
        if (program) {
          const allEvidenceParts = EvidenceUtils.flattenEvidenceParts(evidenceParts);
          const approvalTriggerPart = allEvidenceParts.find(
            (p) => p.ApprovalTriggerValue && evidenceJSON?.[p.Name] === p.ApprovalTriggerValue
          );
          const rejectionTriggerPart = allEvidenceParts.find(
            (p) => p.ApprovalTriggerValue && evidenceJSON?.[p.Name] && evidenceJSON?.[p.Name] !== p.ApprovalTriggerValue
          );
          let computedApprovalState = isDraftEvidence
            ? undefined
            : approvalTriggerPart ? "Approved" : rejectionTriggerPart ? "Rejected" : approvalState;

          if (!isDraftEvidence && ProgramUtils.isUOCApprenticeship(program.ID)) {
            const supervisorRejectionTriggerPart = allEvidenceParts.find(
              (p) =>
                p.ApprovalTriggerValue &&
                p.FilledInBySupervisor &&
                evidenceJSON?.[p.Name] &&
                evidenceJSON?.[p.Name] !== p.ApprovalTriggerValue
            );
            const isSynchronousAssurance = Object.entries(evidenceJSON ?? {}).some(
              ([key, value]) =>
                typeof key === "string" &&
                key.endsWith(" Assurance Type") &&
                typeof value === "string" &&
                value.includes("(synchronous)")
            );
            if (
              EvidenceUtils.shouldSetApprovalStateToPending(evidenceJSON, allEvidenceParts) &&
              !supervisorRejectionTriggerPart &&
              !isSynchronousAssurance
            ) {
              computedApprovalState = "Pending";
            }
          }

          const expiryDate =
            computedApprovalState === "Pending" && program.EvidenceExpiryDays
              ? addDays(new Date(), program.EvidenceExpiryDays)
              : undefined;

          const programInfoFromParts: Record<string, string> = {};
          for (const part of allEvidenceParts) {
            if (part.ProgramInfoKey && evidenceJSON?.[part.Name]) {
              programInfoFromParts[part.ProgramInfoKey] = evidenceJSON[part.Name] as string;
            }
          }

          const evidence: IEvidence = {
            date: date.toISOString(),
            ...(addToHCPCAudit !== undefined && { addToHCPCAudit }),
            ...(numberOfHours !== "" && {
              numberOfHours: DataController.convertToNumberOfHours(numberOfHours),
            }),
            evidenceJSON: {
              ...evidenceJSON,
              programInfo: {
                program: program.Name,
                programID: program.ID,
                competence: competence?.Name,
                competenceID: competence?.ID,
                progressCheck: progressCheck?.Name,
                progressCheckID: progressCheck?.ID,
                progressCheckCompetence: progressCheckCompetence?.Name,
                progressCheckCompetenceID: progressCheckCompetence?.ID,
                programEvidenceDefinitionID: programEvidenceDefinition?.ID,
                ...(currentProgramCycle !== undefined && { programCycle: currentProgramCycle }),
                ...(computedApprovalState && { approvalState: computedApprovalState, locked: true }),
                ...(expiryDate && { expiryDate }),
                ...(autoFilledSkill && {
                  skillID: autoFilledSkill.ID,
                  skillName: autoFilledSkill.Name,
                  ...(autoFilledSkill.Level && { year: autoFilledSkill.Level }),
                }),
                ...programInfoFromParts,
              },
            },
            id: id || DataController.generateUniqueID(),
            draft: isDraftEvidence ?? false,
            ...(isAIAssisted && { aiAssisted: true }),
          };

          evidence.evidenceJSON = EvidenceUtils.deleteNonRequiredEvidencePart(evidence.evidenceJSON, evidenceParts);

          if (progressCheckCompetence && !EvidenceUtils.checkEvidenceJSONHasTitle(evidence.evidenceJSON)) {
            evidence.evidenceJSON = EvidenceUtils.addTitleToProgressCheck(
              evidence.evidenceJSON,
              progressCheckCompetence
            );
          } else if (isDraftEvidence && !EvidenceUtils.checkEvidenceJSONHasTitle(evidence.evidenceJSON)) {
            const draftsLength = EvidenceUtils.getDraftsCount(_e.contents);

            evidence.evidenceJSON.Title = `My Draft #${draftsLength ? draftsLength + 1 : "1"}`;
          }

          if (ProgramUtils.isNwasARC(program.ID)) {
            const skillKey = EvidenceUtils.getEvidenceSkillKey(evidence);

            if (skillKey) {
              evidence.evidenceJSON = EvidenceUtils.addSkillTitle(evidence.evidenceJSON, skillKey);
            }

            if (programEvidenceDefinition?.EvidenceSelectionType === "witness-simulation") {
              evidence.evidenceJSON = EvidenceUtils.addWitnessSimulationTitle(evidence.evidenceJSON);
              evidence.evidenceJSON.programInfo.witnessSimulationSkillID = preselectedSkill?.ID ?? undefined;
            }
          }

          if (ProgramUtils.isSPAPRP(program.ID) && EvidenceUtils.isSkillsSelectEvidence(programEvidenceDefinition)) {
            const newTitle = SPAPRPUtils.createSkillsEvidenceTitle({
              evidence,
              preselectedSkill,
              draft: isDraftEvidence ?? false,
              draftsLength: EvidenceUtils.getDraftsCount(_e.contents),
            });

            if (newTitle) {
              evidence.evidenceJSON.Title = newTitle;
            }
          }

          if (EvidenceUtils.isWPBAFormEvidence(programEvidenceDefinition)) {
            const newTitle = SPAPRPUtils.createWPBAFormEvidenceTitle({
              form: wpbaForm,
              draft: isDraftEvidence ?? false,
              draftsLength: EvidenceUtils.getDraftsCount(_e.contents),
            });

            if (newTitle) {
              evidence.evidenceJSON.Title = newTitle;
            }

            if (evidence.evidenceJSON.programInfo.pending === undefined && !isDraftEvidence) {
              evidence.evidenceJSON.programInfo.pending = true;
              if (program.EvidenceExpiryDays) {
                evidence.evidenceJSON.programInfo.expiryDate = addDays(
                  new Date(),
                  program.EvidenceExpiryDays
                ).toISOString();
              }
            }
          }

          if (isDraftEvidence && EvidenceUtils.checkEvidenceJSONHasTitle(evidence.evidenceJSON)) {
            evidence.title = evidence.evidenceJSON.Title;
          }

          try {
            // Find if there are any Evidence Parts that can hold attachments (support multiple fields)
            const attachmentPartKeys = ProgramUtils.getEvidenceAttachmentsKeys
              ? ProgramUtils.getEvidenceAttachmentsKeys(evidenceParts)
              : ([ProgramUtils.getEvidenceAttachmentsKey(evidenceParts)].filter(Boolean) as string[]);

            for (const attachmentPartKey of attachmentPartKeys) {
              const hasAttachments = typeof evidence.evidenceJSON[attachmentPartKey] !== "undefined";

              if (hasAttachments) {
                if (evidence.evidenceJSON[attachmentPartKey].attachmentsToAdd?.length > 0) {
                  setHandlingAttachments(true);
                  const _attachments = _.clone(evidence.evidenceJSON[attachmentPartKey].attachments) || [];

                  const uploaded = await uploadAttachments(
                    evidence.evidenceJSON[attachmentPartKey].attachmentsToAdd,
                    evidence.id
                  );

                  for (let i = 0; i < uploaded.length; i++) {
                    const index = _attachments?.findIndex((item: EvidenceAttachment) => item.name === uploaded[i].name);

                    if (index > -1) {
                      _attachments[index] = uploaded[i];
                    } else {
                      _attachments.push(uploaded[i]);
                    }
                  }

                  evidence.evidenceJSON[attachmentPartKey].attachments = _attachments;
                  delete evidence.evidenceJSON[attachmentPartKey].attachmentsToAdd;
                } else {
                  delete evidence.evidenceJSON[attachmentPartKey].attachmentsToAdd;
                }

                if (evidence.evidenceJSON[attachmentPartKey].attachmentsToDelete?.length > 0) {
                  setHandlingAttachments(true);
                  const _attachments = _.clone(evidence.evidenceJSON[attachmentPartKey].attachments) || [];

                  const deleted = await deleteAttachments(
                    evidence.evidenceJSON[attachmentPartKey].attachmentsToDelete,
                    evidence.id
                  );

                  for (let i = 0; i < deleted.length; i++) {
                    const index = _attachments?.findIndex((item: EvidenceAttachment) => item.name === deleted[i]);

                    if (typeof index === "number" && index > -1) {
                      _attachments?.splice(index, 1);
                    }
                  }

                  evidence.evidenceJSON[attachmentPartKey].attachments = _attachments;
                  delete evidence.evidenceJSON[attachmentPartKey].attachmentsToDelete;
                } else {
                  delete evidence.evidenceJSON[attachmentPartKey].attachmentsToDelete;
                }
              }
            }
          } catch (error) {
            console.log(error);
          }

          setHandlingAttachments(false);

          try {
            await FirebaseService.logEvent("program_evidence_added", {
              program: program?.Name,
              competence: competence?.Name,
              includedInAudit: addToHCPCAudit,
              draft: isDraftEvidence ?? false,
            });
          } catch (error) {
            console.log(error);
          }

          // TODO: Add automatic LOs for NQP2 competences

          if (progressCheckCompetence?.["LOs Automatic"]) {
            const selectedOutcomes = progressCheckCompetence["Learning Objective"]?.map((item) => ({
              id: item.ID,
              name: item.Name,
              type: "automatic",
            }));

            evidence.evidenceJSON = {
              LearningObjectivesTickboxes: {
                selectedOutcomes,
              },
              ...evidence.evidenceJSON,
            };
          }

          try {
            let addToDB = await DatabaseService.addProgramEvidence(user.contents, evidence);

            if (!addToDB) {
              addToDB = await DatabaseService.updateProgramEvidence(user.contents, evidence);
            }

            if (addToDB) {
              const refreshedEvidence = await refreshUserEvidence(user.contents);

              if (evidence.evidenceJSON.programInfo.progressCheckID) {
                const progressCheckStatus = ProgramUtils.getProgressCheckStatusFromEvidence(
                  evidence.evidenceJSON.programInfo.progressCheckID,
                  allProgressCheckData.contents,
                  program
                );

                if (!isDraftEvidence) {
                  if (!progressCheckStatus) {
                    // add new progress check

                    let progressCheckCreated;

                    if (
                      ProgramUtils.autoCompleteProgressCheck(
                        evidence.evidenceJSON.programInfo.progressCheckCompetenceID
                      )
                    ) {
                      progressCheckCreated = await ProgressCheckService.completeProgressCheck(
                        user.contents,
                        program.ID,
                        evidence.evidenceJSON.programInfo.progressCheckID
                      );
                    } else {
                      if (EvidenceUtils.evidencePartTriggerFound(evidenceParts)) {
                        const operation = EvidenceUtils.evidencePartTriggerUsed(evidenceParts, evidenceJSON)
                          ? "Completed"
                          : "OnHoldReset";
                        await ProgressCheckService.createProgressCheck(
                          user.contents,
                          program.ID,
                          evidence.evidenceJSON.programInfo.progressCheckID,
                          operation
                        );
                      } else if (
                        ProgramUtils.isClinicalAdvisor(program.ID) &&
                        progressCheckCompetence?.isForMigration
                      ) {
                        const operation = "Completed";
                        await ProgressCheckService.createProgressCheck(
                          user.contents,
                          program.ID,
                          evidence.evidenceJSON.programInfo.progressCheckID,
                          operation
                        );
                      } else {
                        if (ProgramUtils.isUOCApprenticeship(program.ID)) {
                          // PC lifecycle for UoC is managed server-side via the EoY review.
                          // Fetch fresh data before creating as "In-Progress" to avoid downgrading
                          // a PC that was just set to "Submitted" by the server.
                          const freshData = await refreshProgressCheckData(user.contents);
                          const freshStatus = ProgramUtils.getProgressCheckStatusFromEvidence(
                            evidence.evidenceJSON.programInfo.progressCheckID,
                            freshData,
                            program
                          );
                          if (!freshStatus) {
                            progressCheckCreated = await ProgressCheckService.createProgressCheck(
                              user.contents,
                              program.ID,
                              evidence.evidenceJSON.programInfo.progressCheckID,
                              "In-Progress"
                            );
                          }
                        } else {
                          let operation = "In-Progress";

                          const evidenceForProgressCheck = ProgramUtils.getEvidenceForProgressCheck(
                            refreshedEvidence,
                            evidence.evidenceJSON.programInfo.progressCheckID
                          );

                          if (
                            progressCheck &&
                            progressCheck.Approval === ProgressCheckApproval.NotRequired &&
                            ProgramUtils.isAllRequiredEvidenceAddedForProgressCheck(
                              evidenceForProgressCheck,
                              progressCheck["Competence/Activity"]
                            )
                          ) {
                            operation = "Completed";
                          }

                          let cycle;

                          if (ProgramUtils.isSPAPRP(program.ID)) {
                            cycle = currentProgramCycle;
                          }

                          progressCheckCreated = await ProgressCheckService.createProgressCheck(
                            user.contents,
                            program.ID,
                            evidence.evidenceJSON.programInfo.progressCheckID,
                            operation,
                            cycle
                          );
                        }
                      }
                    }

                    progressCheckCreated && (await refreshProgressCheckData(user.contents));
                  } else {
                    // update existing progress check

                    // Do special check for CSM because we want to avoid email spam from server (informing users of complete progress checks).
                    // CSM is the only Telephone Triage module that can receive evidence after the review is done
                    if (!ProgramUtils.isCSM(program.ID) || progressCheckStatus.submissions.status !== "Completed") {
                      if (ProgramUtils.autoCompleteProgressCheckDependsOnTrigger(program.ID)) {
                        if (EvidenceUtils.evidencePartTriggerFound(evidenceParts) && !isDraftEvidence) {
                          const operation = EvidenceUtils.evidencePartTriggerUsed(evidenceParts, evidenceJSON)
                            ? "Completed"
                            : "OnHoldReset";

                          await ProgressCheckService.createProgressCheck(
                            user.contents,
                            program.ID,
                            evidence.evidenceJSON.programInfo.progressCheckID,
                            operation
                          );
                        } else if (
                          ProgramUtils.isClinicalAdvisor(program.ID) &&
                          progressCheckCompetence?.isForMigration
                        ) {
                          const operation = "Completed";
                          await ProgressCheckService.createProgressCheck(
                            user.contents,
                            program.ID,
                            evidence.evidenceJSON.programInfo.progressCheckID,
                            operation
                          );
                        } else {
                          let operation = progressCheckStatus.submissions.status;
                          if (!isDraftEvidence && operation === "OnHoldReset") {
                            operation = "In-Progress";
                          }
                          await ProgressCheckService.createProgressCheck(
                            user.contents,
                            program.ID,
                            evidence.evidenceJSON.programInfo.progressCheckID,
                            operation
                          );
                        }
                        await refreshProgressCheckData(user.contents);
                      } else if (progressCheck?.Approval === ProgressCheckApproval.NotRequired) {
                        const evidenceForProgressCheck = ProgramUtils.getEvidenceForProgressCheck(
                          refreshedEvidence,
                          evidence.evidenceJSON.programInfo.progressCheckID
                        );

                        if (
                          ProgramUtils.isAllRequiredEvidenceAddedForProgressCheck(
                            evidenceForProgressCheck,
                            progressCheck["Competence/Activity"]
                          )
                        ) {
                          const progressCheckCompleted = await ProgressCheckService.completeProgressCheck(
                            user.contents,
                            program.ID,
                            evidence.evidenceJSON.programInfo.progressCheckID
                          );

                          progressCheckCompleted && (await refreshProgressCheckData(user.contents));
                        }
                      } else if (ProgramUtils.isSPAPRP(program.ID)) {
                        let operation = progressCheckStatus.submissions.status;

                        if (!isDraftEvidence && operation === "Not-started") {
                          operation = "In-Progress";

                          await ProgressCheckService.createProgressCheck(
                            user.contents,
                            program.ID,
                            evidence.evidenceJSON.programInfo.progressCheckID,
                            operation
                          );
                        }
                      }
                    }
                  }
                }
              }

              if (isDraftEvidence) {
                setId(evidence.id);
                setSavingDraft(false);
                setIsDraft(true);
                setIsNewEvidence(false);
                EventRegister.emit("evidence/draft-saved");
              } else {
                if (ProgramUtils.isNwasARC(program.ID)) {
                  createPreviousIncidentDetailsObject({ evidenceParts, evidenceJSON });
                }

                setSaving(false);

                if (ProgramUtils.isUOCApprenticeship(program.ID) && !!evidence.evidenceJSON?.programInfo?.expiryDate) {
                  setConfirmationEvidence(evidence);
                } else {
                  dismissModal();
                }
              }
            } else {
              if (isDraftEvidence) {
                setSavingDraft(false);
              } else {
                setSaving(false);
              }

              window.alert("An error occurred when saving evidence");
            }
          } catch (error) {
            if (isDraftEvidence) {
              setSavingDraft(false);
            } else {
              setSaving(false);
            }
            console.log(error);
            window.alert("An error occurred when saving evidence");
          }
        } else {
          let evidence: IEvidence = {
            title: title,
            date: date.toISOString(),
            ...(activity && { activity }),
            ...(numberOfHours !== "" && {
              numberOfHours: DataController.convertToNumberOfHours(numberOfHours),
            }),
            ...(standardTags.length > 0 && { standardTags }),
            ...(customTags.length > 0 && {
              customTags: customTags.map((item) => ({
                tag: item.tag,
              })),
            }),
            ...(attachments.length > 0 && { attachments }),
            ...(activityDescription !== "" && {
              activityDescription: activityDescription,
            }),
            benefits: benefits,
            reflections: reflections,
            ...(objectives !== "" && {
              objectives: objectives,
            }),
            ...(addToHCPCAudit !== undefined && { addToHCPCAudit }),
            ...(timelineReference !== "" && { timelineReference }),
            id: id || DataController.generateUniqueID(),
            draft: isDraftEvidence ?? false,
            ...(dictationInput.length > 0 && { dictationInput }),
            ...(isAIAssisted && { aiAssisted: true }),
          };

          if (activityDateMode === "single") {
            evidence.activityDate = activitySingleDate.toISOString();
          } else if (activityDateMode === "range") {
            if (activityDateRange?.from) {
              evidence.activityDate = activityDateRange.from.toISOString();
            }

            if (activityDateRange?.to) {
              evidence.activityDateEnd = activityDateRange.to.toISOString();
            }
          }

          if (isDraftEvidence && EvidenceUtils.isEvidenceTitleBlank(evidence.title)) {
            const draftsLength = EvidenceUtils.getDraftsCount(_e.contents);

            evidence.title = `My Draft #${draftsLength ? draftsLength + 1 : "1"}`;
            setTitle(evidence.title);
          }

          try {
            if (attachmentsToAdd.length > 0) {
              const _attachments = evidence.attachments && _.clone(evidence.attachments);
              setHandlingAttachments(true);

              const uploaded = await uploadAttachments(attachmentsToAdd, evidence.id);

              for (let i = 0; i < uploaded.length; i++) {
                const index = _attachments?.findIndex((item: EvidenceAttachment) => item.name === uploaded[i].name);

                if (_attachments && typeof index === "number" && index > -1) {
                  _attachments[index] = uploaded[i];
                }
              }

              evidence = {
                ...evidence,
                attachments: _attachments,
              };
            }
          } catch (error) {
            console.log(error);
          }

          try {
            if (attachmentsToDelete.length > 0) {
              const _attachments = evidence.attachments && _.clone(evidence.attachments);
              setHandlingAttachments(true);

              const deleted = await deleteAttachments(attachmentsToDelete, evidence.id);

              for (let i = 0; i < deleted.length; i++) {
                const index = _attachments?.findIndex((item: EvidenceAttachment) => item.name === deleted[i]);

                if (typeof index === "number" && index > -1) {
                  _attachments?.splice(index, 1);
                }
              }

              evidence = {
                ...evidence,
                attachments: _attachments,
              };
            }
          } catch (error) {
            console.log(error);
          }

          try {
            await FirebaseService.logEvent("evidence_added", {
              activity: activity?.type,
              ...(evidence.attachments && {
                has_attachments: true,
                attachments: evidence.attachments.length,
              }),
              ...(evidence.standardTags &&
                evidence.standardTags.length > 0 && {
                  standards: JSON.stringify(evidence.standardTags.map((item) => item.tag)),
                }),
              ...(evidence.customTags &&
                evidence.customTags.length > 0 && {
                  tags: JSON.stringify(evidence.customTags.map((item) => item.tag)),
                }),
              tags_added: evidence.customTags && evidence.customTags?.length > 0,
              hasReflection: reflections.length > 0,
              hasBenefits: benefits.length > 0,
              hasLearningObjectives: objectives.length > 0,
              includedInAudit: addToHCPCAudit,
              userTrust: user.contents?.userData.CFRTrust,
              timelineReference: evidence.timelineReference,
              draft: isDraftEvidence ?? false,
            });
          } catch (error) {
            console.log(error);
          }

          setHandlingAttachments(false);

          try {
            let addToDB = await DatabaseService.addEvidence(user.contents, evidence);

            if (!addToDB) {
              addToDB = await DatabaseService.updateEvidence(user.contents, evidence);
            }

            if (addToDB) {
              await refreshUserEvidence(user.contents);
              resetDictation();

              if (isDraftEvidence) {
                setId(evidence.id);
                setSavingDraft(false);
                setIsDraft(true);
                setIsNewEvidence(false);
                EventRegister.emit("evidence/draft-saved");
              } else {
                setSaving(false);
                dismissModal();
              }
            } else {
              if (isDraftEvidence) {
                setSavingDraft(false);
              } else {
                setSaving(false);
              }
              window.alert("An error occurred when saving evidence");
            }
          } catch (error) {
            if (isDraftEvidence) {
              setSavingDraft(false);
            } else {
              setSaving(false);
            }
            window.alert("An error occurred when saving evidence");
          }
        }
      }
    } catch (error) {
      if (isDraftEvidence) {
        setSavingDraft(false);
      } else {
        setSaving(false);
      }
      console.log(error);
      window.alert("An error occurred when saving evidence");
    }
  };

  const deleteClicked = async (): Promise<void> => {
    changeActivityState();
    setDeleteEvidenceModalVisible(true);
  };

  const saveDraftClicked = async (): Promise<void> => {
    changeActivityState();
    try {
      await saveEvidence(true);
    } catch (error) {
      console.log(error);
    }
  };

  const saveDraftIfChanged = async (): Promise<void> => {
    if (isDraft) {
      await saveEvidence(true);
    } else if (isNewEvidence) {
      setCanSaveDraft(true);
    }
  };

  const saveEvidenceAndClose = async (): Promise<void> => {
    const mandatoryFieldsCheck = areAllMandatoryFieldsComplete();
    if (mandatoryFieldsCheck.complete) {
      await saveEvidence();
      dismissModal();
    } else {
      setUnsavedChangesModalVisible(false);
      setShowErrorBanner(true);
      setIncompleteMandatoryFields([...mandatoryFieldsCheck.incompleteFields]);
      setActiveMandatoryFieldIndex(0);
      scrollToMandatoryField(0, mandatoryFieldsCheck.incompleteFields);
    }
  };

  const saveDraftAndClose = async (): Promise<void> => {
    await saveDraftClicked();
    dismissModal();
  };

  const deleteEvidenceAndClose = async (): Promise<void> => {
    await deleteEvidence();
  };

  const draftCloseClicked = async (): Promise<void> => {
    await saveDraftAndClose();
  };

  const newEvidenceCloseClicked = (): void => {
    changeActivityState();
    if (canSaveDraft) {
      setSaveDraftModalVisible(true);
    } else {
      dismissModal();
    }
  };

  const cancelClicked = (): void => {
    if (program) {
      if (_.isEmpty(evidenceJSON)) {
        dismissModal();
      } else {
        if (!_.isEmpty(initialEvidenceJSON)) {
          const tempJSON = _.cloneDeep(evidenceJSON);

          // Find values that are dates and convert to ISO string
          // that database holds before checking equality
          const dates = Object.keys(tempJSON).filter((key) => tempJSON[key] instanceof Date);

          for (let i = 0; i < dates.length; i++) {
            const key = dates[i];
            tempJSON[key] = tempJSON[key].toISOString();

            if (isSameDay(new Date(tempJSON[key]), new Date(initialEvidenceJSON[key]))) {
              tempJSON[key] = initialEvidenceJSON[key];
            }
          }

          if (_.isEqual(initialEvidenceJSON, tempJSON)) {
            dismissModal();
          } else {
            setUnsavedChangesModalVisible(true);
          }
        } else {
          dismissModal();
        }
      }
    } else {
      // initial evidence should exist at this point

      const evidence: IEvidence = {
        title,
        date: date.toISOString(),
        ...(activity && { activity }),
        ...(numberOfHours !== "" && {
          numberOfHours: DataController.convertToNumberOfHours(numberOfHours),
        }),
        standardTags: standardTags.length > 0 ? standardTags : [],
        customTags: customTags.length > 0 ? customTags : [],
        attachments: attachments.length > 0 ? attachments : [],
        reflections,
        ...(activityDescription !== "" && {
          activityDescription: activityDescription,
        }),
        benefits,
        ...(objectives && { objectives }),
        ...(addToHCPCAudit !== undefined && { addToHCPCAudit }),
        ...(timelineReference !== "" && { timelineReference }),
        id: id || DataController.generateUniqueID(),
      };

      if (activityDateMode === "single") {
        evidence.activityDate = activitySingleDate.toISOString();
      } else if (activityDateMode === "range") {
        const activityDateArray = [];

        if (activityDateRange?.from) {
          activityDateArray.push(activityDateRange.from.toISOString());
        }

        if (activityDateRange?.to) {
          activityDateArray.push(activityDateRange.to.toISOString());
        }

        if (activityDateArray.length > 0) {
          evidence.activityDate = activityDateArray.join(";");
        }
      }

      if (typeof initialEvidence!.attachments === "undefined" && evidence.attachments?.length === 0) {
        delete evidence.attachments;
      }

      if (typeof initialEvidence!.standardTags === "undefined" && evidence.standardTags?.length === 0) {
        delete evidence.standardTags;
      }

      if (typeof initialEvidence!.addToHCPCAudit === "undefined" && !evidence.addToHCPCAudit) {
        delete evidence.addToHCPCAudit;
      }

      if (typeof initialEvidence!.customTags === "undefined" && evidence.customTags?.length === 0) {
        delete evidence.customTags;
      }

      const noChanges = _.isEqual(initialEvidence!, evidence);

      if (!noChanges) {
        // changes have been made to a piece of personal evidence
        setUnsavedChangesModalVisible(true);
      } else {
        dismissModal();
      }
    }
  };

  const dismissModal = (): void => {
    closeInternalDialogs();
    closeModal(false);
  };

  const closeInternalDialogs = (): void => {
    setSaveDraftModalVisible(false);
    setEmptyTagModalVisible(false);
    setExistingTagModalVisible(false);
    setUnsavedChangesModalVisible(false);
    setDeleteEvidenceModalVisible(false);
  };

  const resetModalState = (): void => {
    setTitle("");
    setDate(new Date());
    setActivitySingleDate(new Date());
    setActivityDateRange(undefined);
    setActivityDateMode("single");
    setActivity(null);
    setNumberOfHours("");
    setStandardTags([]);
    setCustomTags([]);
    setAttachments([]);
    setAttachmentsToAdd([]);
    setAttachmentsToDelete([]);
    setActivityDescription("");
    setReflection("");
    setBenefits("");
    setObjectives("");
    setDictationInput("");
    setAddToHCPCAudit(false);
    setTimelineReference("");
    setId("");
    setInitialEvidence(null);
    setSaving(false);
    setSavingDraft(false);
    setCanSaveDraft(false);
    setIsNewEvidence(true);
    setIsDraft(false);
    setTempInput("");
    setSaveDraftModalVisible(false);
    setDeleteEvidenceModalVisible(false);
    setEmptyTagModalVisible(false);
    setExistingTagModalVisible(false);
    setConfirmationEvidence(null);
    setProgram(null);
    setCompetence(null);
    setEnhancedSkill(null);
    setCompetences([]);
    setEvidenceParts([]);
    setProgressCheck(null);
    setProgressChecks([]);
    setProgressCheckCompetences([]);
    setProgressCheckCompetence(null);
    setProgramMandatoryStandards([]);
    setProgramOptionalStandards([]);
    setProgramSkills([]);
    setPreselectedSkill(null);
    setPreselectedLearningObjective(null);
    setExpandStandardId(null);
    setProgramLearningObjectives([]);
    setProgramEvidenceDefinition(null);
    setApprovalState(null);
    setAutoFilledSkill(null);
    setEvidenceJSON({});
    setInitialEvidenceJSON({});
    setDisableProgressCheckDropdown(false);
    setDisableProgressCheckCompetenceDropdown(false);
    setShowErrorBanner(false);
    setWpbaForm(null);
    mandatoryFieldRefs.current = {};
    changeActivityState();
    setIncompleteMandatoryFields([]);
    setActiveMandatoryFieldIndex(0);
    setAiAssistSessionId(null);
    setAiAssistMessages([]);
    setIsAIAssisted(false);
    resetLatestEvidencePreview();
    closeModal(false);
  };

  const discardDraft = () => {
    FirebaseService.logEvent("draft_evidence_discarded", {});
    dismissModal();
  };

  const openDatePicker = (): void => {
    setDatePickerVisible(true);
    changeActivityState();
  };

  const openActivityDatePicker = (): void => {
    setActivityDatePickerVisible(true);
    changeActivityState();
  };

  const handleDateRangeModeChange = (newMode: "single" | "range"): void => {
    if (newMode === activityDateMode) return;

    if (newMode === "single") {
      if (activityDateRange?.from) {
        setActivitySingleDate(activityDateRange.from);
      }
      setActivityDateMode("single");
    }

    if (newMode === "range") {
      if (activitySingleDate) {
        setActivityDateRange({ from: activitySingleDate });
      }
      setActivityDateMode("range");
    }
  };

  const handleDateRangeChange = (triggerDate: Date): void => {
    if (!activityDateRange) {
      setActivityDateRange({ from: triggerDate });
      return;
    }

    const copy = { ...activityDateRange };
    const triggerIsBeforeFromDate = isBefore(triggerDate, copy.from);
    const triggerIsAfterFromDate = isAfter(triggerDate, copy.from);
    if (copy.to || triggerIsBeforeFromDate) {
      copy.from = triggerDate;
      copy.to = undefined;
    } else if (triggerIsAfterFromDate) {
      copy.to = triggerDate;
    }

    setActivityDateRange(copy);
  };

  const viewAttachment = async (attachment: EvidenceAttachment): Promise<void> => {
    try {
      if (attachment.url && user.contents) {
        setViewingAttachment(true);
        setViewingAttachmentFilename(attachment.name);

        await FileUtils.viewAttachment(user.contents, id, attachment);

        setViewingAttachment(false);
        setViewingAttachmentFilename("");
      }
    } catch (error) {
      console.log(error);
      setViewingAttachment(false);
      setViewingAttachmentFilename("");
      window.alert("An error occurred when opening attachment");
    }
  };

  const deleteAttachment = (name: string): void => {
    const index = attachments.findIndex((attachment: EvidenceAttachment) => attachment?.name === name);

    if (index > -1) {
      const attachment = attachments[index];

      if (attachment.url) {
        const toDelete = _.cloneDeep(attachmentsToDelete);
        toDelete.push(attachment);

        setAttachmentsToDelete(toDelete);
      } else if (attachment.content) {
        const toAdd = _.cloneDeep(attachmentsToAdd);
        const addedIndex = _.findIndex(toAdd, (item) => item.name === name);
        addedIndex > -1 && toAdd.splice(addedIndex);

        setAttachmentsToAdd(toAdd);
      }
    }

    index !== -1 && setAttachments(attachments.filter((_, i) => i !== index));
  };

  const deleteCustomTag = (value: string): void => {
    const index = customTags.findIndex((tag: EvidenceCustomTag) => tag.tag === value);

    index !== -1 && setCustomTags(customTags.filter((_, i) => i !== index));
    saveDraftIfChanged();
  };

  const reflectionsEditorFocused = (): void => {
    setReflectionsFocused(true);
    setTempInput(reflections);
    const editor = document.getElementById("reflectionsEditorRef");
    editor && editor.scrollIntoView();
    changeActivityState();
  };

  const reflectionsEditorBlurred = (): void => {
    setReflectionsFocused(false);

    if (tempInput !== reflections) {
      saveDraftIfChanged();
    }

    setTempInput("");
  };

  const benefitsEditorFocused = (): void => {
    setBenefitsFocused(true);
    setTempInput(benefits);
    const editor = document.getElementById("benefitsEditorRef");
    editor && editor.scrollIntoView();
    changeActivityState();
  };

  const benefitsEditorBlurred = (): void => {
    setBenefitsFocused(false);

    if (tempInput !== benefits) {
      saveDraftIfChanged();
    }

    setTempInput("");
  };

  const objectivesEditorFocused = (): void => {
    setObjectivesFocused(true);
    setTempInput(objectives);
    const editor = document.getElementById("objectivesEditorRef");
    editor && editor.scrollIntoView();
    changeActivityState();
  };

  const objectivesEditorBlurred = (): void => {
    setObjectivesFocused(false);

    if (tempInput !== objectives) {
      saveDraftIfChanged();
    }

    setTempInput("");
  };

  const titleChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setTitle(event.target.value);
    isNewEvidence && setCanSaveDraft(true);
  };

  const titleFocused = (event: React.FocusEvent<HTMLInputElement>): void => {
    setTempInput(event.target.value);
    changeActivityState();
  };

  const titleBlurred = (): void => {
    if (tempInput !== title) {
      saveDraftIfChanged();
    }

    setTempInput("");
  };

  const activityChanged = (event: any): void => {
    event.detail.value && setActivity({ type: event.detail.value });
    saveDraftIfChanged();
  };

  const onDurationPickerChange = useCallback((newTime: string) => {
    setNumberOfHours(newTime);
    saveDraftIfChanged();
  }, []);

  const activityDescriptionChanged = (event: any): void => {
    setActivityDescription(event.target.value);
    isNewEvidence && setCanSaveDraft(true);
  };

  const activityDescriptionFocused = (event: any) => {
    setTempInput(event.target.value);
    changeActivityState();
  };

  const activityDescriptionBlurred = (): void => {
    if (tempInput !== activityDescription) {
      saveDraftIfChanged();
    }

    setTempInput("");
  };

  const saveNewTag = (newTag: string): void => {
    if (newTag.trim() === "") {
      setEmptyTagModalVisible(true);
    } else if (!customTags.find((tag) => tag.tag.toUpperCase() === newTag.toUpperCase().trim())) {
      setCustomTags((tags) => [...tags, { tag: newTag.trim() }]);
      saveDraftIfChanged();
    } else {
      setExistingTagModalVisible(true);
    }
  };

  const areAllMandatoryFieldsComplete = (): { complete: boolean; incompleteFields: string[] } => {
    if (program) {
      const allParts = EvidenceUtils.flattenEvidenceParts(evidenceParts);
      let mandatory: EvidencePart[] = _.cloneDeep(allParts) || [];
      mandatory = mandatory.filter(
        (item) => item["Field type"] !== FieldType.Banner && item["Field type"] !== FieldType.SkillsDisplay
      );
      mandatory = mandatory.filter((item) => !item?.FilledInBySupervisor);
      mandatory = mandatory.filter((item) => item?.options === "Mandatory" || item?.["option type"] === "Mandatory");
      mandatory = mandatory.filter((item) => {
        const parentPart = allParts.find((part: EvidencePart) =>
          part["Linked Part"]?.find((linkedPart: EvidencePart) => linkedPart.ID === item.ID)
        );
        const required = EvidenceUtils.checkIfEvidencePartIsRequired(item, evidenceJSON, parentPart);

        return required;
      });

      const mandatoryEvidenceParts: { done: boolean; partName: string }[] = [];

      for (let i = 0; i < mandatory.length; i++) {
        const part = mandatory[i];
        const value = evidenceJSON[`${part.Name}`];

        let done = false;
        if (part["Field type"] === FieldType.NOSSelect) {
          done = EvidenceUtils.checkNOSSelectIsFilled(evidenceJSON);
          mandatoryEvidenceParts.push({ done, partName: part.Name });
        } else if (
          part["Field type"] === FieldType.LearningOutcomesList ||
          part["Field type"] === FieldType.LearningOutcomesListSegmented
        ) {
          done = EvidenceUtils.checkLearningOutcomesIsFilled(evidenceJSON);
          mandatoryEvidenceParts.push({ done, partName: part.Name });
        } else if (part["Field type"] === FieldType.LOsMultiSelectForNQP) {
          done = EvidenceUtils.checkLOsMultiSelectForNQPIsFilled(evidenceJSON);
          mandatoryEvidenceParts.push({ done, partName: part.Name });
        } else {
          done = DataController.checkEvidencePartIsFilled(part["Field type"] ?? "", value);
          mandatoryEvidenceParts.push({ done, partName: part.Name });
        }
      }

      const complete = mandatoryEvidenceParts.length > 0 ? mandatoryEvidenceParts.every((item) => item.done) : true;
      return {
        complete,
        incompleteFields: mandatoryEvidenceParts.filter((item) => !item.done).map((item) => item.partName),
      };
    } else {
      const incompleteFields: string[] = [];
      if (!title.length) incompleteFields.push("Title");
      if (!date) incompleteFields.push("Date of reflection");
      if (activityDateMode === "single" && !activitySingleDate) incompleteFields.push("Date of activity");
      if (activityDateMode === "range" && !activityDateRange) incompleteFields.push("Date of activity");
      if (!activity || _.isEmpty(activity)) incompleteFields.push("Activity");
      if (reflections === "") incompleteFields.push("Reflections");

      return { complete: incompleteFields.length === 0, incompleteFields: incompleteFields };
    }
  };

  const isSaveButtonDisabled = (): boolean => {
    if (!program || progressChecks.length === 0) return saving;

    return !progressCheck || !progressCheckCompetence || saving;
  };

  const disableFocusTrap = (): boolean => {
    return reflectionsFocused || benefitsFocused || objectivesFocused || richTextBoxFocused;
  };

  const canDismiss = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (program) {
        if (_.isEmpty(evidenceJSON)) {
          resolve(true);
        } else {
          if (!_.isEmpty(initialEvidenceJSON)) {
            const tempJSON = _.cloneDeep(evidenceJSON);

            // Find values that are dates and convert to ISO string
            // that database holds before checking equality
            const dates = Object.keys(tempJSON).filter((key) => tempJSON[key] instanceof Date);

            for (let i = 0; i < dates.length; i++) {
              const key = dates[i];
              tempJSON[key] = tempJSON[key].toISOString();

              if (isSameDay(new Date(tempJSON[key]), new Date(initialEvidenceJSON[key]))) {
                tempJSON[key] = initialEvidenceJSON[key];
              }
            }

            resolve(true);
          } else {
            resolve(true);
          }
        }
      } else {
        if (initialEvidence) {
          const evidence: IEvidence = {
            title,
            date: date.toISOString(),
            ...(activity && { activity }),
            ...(numberOfHours !== "" && {
              numberOfHours: DataController.convertToHoursMinutes(numberOfHours),
            }),
            standardTags: standardTags.length > 0 ? standardTags : [],
            customTags: customTags.length > 0 ? customTags : [],
            attachments: attachments.length > 0 ? attachments : [],
            reflections,
            ...(activityDescription !== "" && {
              activityDescription: activityDescription,
            }),
            benefits,
            ...(objectives && { objectives }),
            ...(addToHCPCAudit !== undefined && { addToHCPCAudit }),
            ...(timelineReference !== "" && { timelineReference }),
            id: id || DataController.generateUniqueID(),
          };

          if (activityDateMode === "single") {
            evidence.activityDate = activitySingleDate.toISOString();
          } else if (activityDateMode === "range") {
            if (activityDateRange?.from) {
              evidence.activityDate = activityDateRange.from.toISOString();
            }

            if (activityDateRange?.to) {
              evidence.activityDateEnd = activityDateRange.to.toISOString();
            }
          }

          if (typeof initialEvidence.attachments === "undefined" && evidence.attachments?.length === 0) {
            delete evidence.attachments;
          }

          if (typeof initialEvidence.standardTags === "undefined" && evidence.standardTags?.length === 0) {
            delete evidence.standardTags;
          }

          if (typeof initialEvidence.addToHCPCAudit === "undefined" && !evidence.addToHCPCAudit) {
            delete evidence.addToHCPCAudit;
          }

          if (typeof initialEvidence.customTags === "undefined" && evidence.customTags?.length === 0) {
            delete evidence.customTags;
          }

          const noChanges = _.isEqual(initialEvidence, evidence);

          if (!noChanges) {
            resolve(true);
          } else {
            resolve(true);
          }
        } else {
          const fieldsFilled =
            title.trim().length > 0 ||
            (activity?.type !== undefined && !_.isEmpty(activity)) ||
            standardTags.length > 0 ||
            customTags.length > 0 ||
            attachments.length > 0 ||
            reflections !== "" ||
            benefits !== "" ||
            objectives !== "";

          if (fieldsFilled) {
            resolve(true);
          } else {
            resolve(true);
          }
        }
      }
    });
  };

  // For Bespoke programs
  const enhancedSkillChanged = (item: any): void => {
    const name = item.detail.value;

    const _competence: CompetenceActivityClass = competences.find(
      (item: CompetenceActivityClass) => item.Name === name
    );

    if (_competence) {
      if (competence && competence.Name !== _competence.Name) {
        setChangeTaskingTypeModalVisible(true);

        // Set tasking type name for confirmation;
        setTempTaskingType(name);
      } else {
        setCompetence(_competence);
        isNewEvidence && setCanSaveDraft(true);
        _competence["Evidence Definitions"] ? getEvidenceParts(_competence) : setEvidenceParts([]);
      }
    }
  };

  const progressCheckChanged = (item: any): void => {
    const name = item.detail.value;

    const _progressCheck: ProgressCheck = progressChecks.find((item: ProgressCheck) => item.Name === name);

    if (_progressCheck) {
      if (progressCheck && progressCheck.Name !== _progressCheck.Name) {
        setChangeProgressCheckModalVisible(true);

        setTempProgressCheck(name);
      } else {
        setProgressCheck(_progressCheck);
        setCanSaveDraft(false);
        _progressCheck["Competence/Activity"]
          ? getProgressCheckCompetences(_progressCheck)
          : setProgressCheckCompetences([]);
      }
    }
  };

  const getOldTags = () => {
    const oldTags: string[] = [];
    if (_e.contents) {
      Array.from(_e.contents).forEach((item: any) => {
        if (item.customTags) {
          item.customTags.forEach((item: any) => {
            if (!oldTags.includes(item.tag)) {
              oldTags.push(item.tag);
            }
          });
        }
      });
    }

    return oldTags;
  };

  const progressCheckCompetenceChanged = (item: any): any => {
    const name = item.detail.value;

    const _progressCheckCompetence: CompetenceActivityClass = progressCheckCompetences.find(
      (item: CompetenceActivityClass) => item.Name === name
    );

    if (_progressCheckCompetence) {
      if (progressCheckCompetence && progressCheckCompetence.Name !== _progressCheckCompetence.Name) {
        setChangeProgressCheckCompetenceModalVisible(true);

        setTempProgressCheckCompetence(name);
      } else {
        const preselectedSkill = ProgramUtils.getPreselectedSkillForCompetence(_progressCheckCompetence, programSkills);

        setProgressCheckCompetence(_progressCheckCompetence);
        preselectedSkill && setPreselectedSkill(preselectedSkill);
        isNewEvidence && setCanSaveDraft(true);
        getEvidenceParts(_progressCheckCompetence);
      }
    }
  };

  const confirmChangeProgressCheck = (name: string): void => {
    if (name.length > 0) {
      const _progressCheck: ProgressCheck = progressChecks.find((item: ProgressCheck) => item.Name === name);

      setCanSaveDraft(false);
      setProgressCheck(_progressCheck);
      setEvidenceParts([]);
      setProgressCheckCompetence(null);
      resetEvidenceAfterTypeChanged();
      _progressCheck["Competence/Activity"]
        ? getProgressCheckCompetences(_progressCheck)
        : setProgressCheckCompetences([]);
    }
  };

  const confirmChangeProgressCheckCompetence = (name: string): void => {
    if (name.length > 0) {
      const _progressCheckCompetence: CompetenceActivityClass = progressCheckCompetences.find(
        (item: CompetenceActivityClass) => item.Name === name
      );
      const preselectedSkill = ProgramUtils.getPreselectedSkillForCompetence(_progressCheckCompetence, programSkills);

      setProgressCheckCompetence(_progressCheckCompetence);
      resetEvidenceAfterTypeChanged();
      preselectedSkill && setPreselectedSkill(preselectedSkill);
      getEvidenceParts(_progressCheckCompetence);
    }
  };

  const confirmChangeTaskType = (name: string) => {
    if (name.length > 0) {
      const _competence: CompetenceActivityClass = competences.find(
        (item: CompetenceActivityClass) => item.Name === name
      );

      setCompetence(_competence);
      resetEvidenceAfterTypeChanged();
      _competence["Evidence Definitions"] ? getEvidenceParts(_competence) : setEvidenceParts([]);
    }
  };

  const resetEvidenceAfterTypeChanged = (): void => {
    setEvidenceJSON({});
    setNumberOfHours("");
    setAddToHCPCAudit(false);
    setChangeProgressCheckModalVisible(false);
    setChangeProgressCheckCompetenceModalVisible(false);
    setChangeTaskingTypeModalVisible(false);
    setShowErrorBanner(false);
    setIncompleteMandatoryFields([]);
    setActiveMandatoryFieldIndex(0);
  };

  const getEvidenceParts = (_competence: CompetenceActivityClass): EvidencePart[] => {
    const evidenceDefinitions = _competence?.["Evidence Definitions"][0];
    const _evidenceParts = EvidenceUtils.getEvidenceParts(evidenceDefinitions);

    setProgramEvidenceDefinition(evidenceDefinitions);
    setEvidenceParts(_evidenceParts);
    return _evidenceParts;
  };

  const addDeepLinkEvidenceParts = (object: INewDeepLinkLearningLoop, evidenceParts: EvidencePart[]): void => {
    const newObject = evidenceParts.reduce((acc, part) => {
      if (part.DeepLinkKey) {
        return {
          ...acc,
          [part.Name]: EvidenceUtils.mapDeepLinkObjectToEvidenceJSON(
            object[part.DeepLinkKey as keyof INewDeepLinkLearningLoop],
            part
          ),
        };
      }
      return acc;
    }, {});

    setEvidenceJSON(newObject);
    setCanSaveDraft(true);
  };

  const blurRichText = (): void => {
    setRichTextBoxFocused(false);
    saveDraftIfChanged();
  };

  const evidencePartChanged = (evidencePart: EvidencePart, value: any, isInput?: boolean): void => {
    const key = evidencePart.Name;

    setEvidenceJSON((prevObject: Record<string, any>) => {
      let newObject = _.cloneDeep(prevObject);

      if (evidencePart["Field type"] === FieldType.Attachments) {
        newObject = _.extend(newObject, {
          [key]: {
            ...newObject[key],
            ...value,
          },
        });
      } else if (evidencePart["Field type"] === FieldType.NOSSelect) {
        newObject = _.extend(newObject, value);
      } else if (evidencePart["Field type"] === FieldType.CompMultiSelect) {
        newObject = _.extend(newObject, { "Comp Selector": value });
      } else if (evidencePart["Field type"] === FieldType.LearningOutcomesList) {
        newObject = _.extend(newObject, { LearningOutcomes: value });
      } else {
        newObject = _.extend(newObject, { [key]: value });
      }

      if (evidencePart["Linked Part"]) {
        if (evidencePart["Linked Part"].length > 1) {
          evidencePart["Linked Part"].forEach((linkedPart) => {
            const triggers = linkedPart["Linked Part trigger"] ? linkedPart["Linked Part trigger"].split("\n") : [];
            if (!triggers.includes(value)) {
              newObject = _.omit(newObject, linkedPart.Name);
            }
          });
        } else if (evidencePart["Linked Part"].length === 1 && evidencePart["Linked Part"][0]) {
          const linkedPart = evidencePart["Linked Part"][0];
          const linkedPartValue = newObject[linkedPart.Name];
          const triggers = evidencePart["Linked Part trigger"] ? evidencePart["Linked Part trigger"].split("\n") : [];

          if (!triggers.includes(linkedPartValue)) {
            // Check if the linked part value is an array
            const currentValue = newObject[linkedPart.Name];
            if (Array.isArray(currentValue)) {
              // Filter out the unmatched value while keeping other values
              const filteredValue = currentValue.filter((v) => v !== value);
              if (filteredValue.length > 0) {
                newObject[linkedPart.Name] = filteredValue;
              } else {
                // If array becomes empty, omit both the linked part and the evidence part
                newObject = _.omit(newObject, [linkedPart.Name, evidencePart.Name]);
              }
            } else {
              // If not an array, omit the linked part
              newObject = _.omit(newObject, [linkedPart.Name]);
            }
          }
        }
      }

      return newObject;
    });

    if (evidencePart.PreviousDetailsKey) {
      const updateResult = updateIncidentDetails({ evidencePart, value, isInput });

      if (updateResult === "PRESENT_PREVIOUS_INCIDENT" && shouldShowReuseIncidentModal()) {
        setReuseIncidentModalVisible(true);
      }
    }

    if (ProgramUtils.isNwasARC(program?.ID ?? "") && EvidenceUtils.isSkillsSelect(evidencePart)) {
      const skill = programSkills.find((skill) => skill.Name === value);

      if (skill?.TriggerPreviousIncidentDetails && shouldShowReuseIncidentModal()) {
        setReuseIncidentModalVisible(true);
      }
    }

    if (evidencePart["Field type"] === FieldType.ActivityDuration) {
      setNumberOfHours(value);
    }

    if (isValid(value) && DataController.isDefaultDate(evidencePart.Name, program)) {
      setDate(value);
    }

    if (!isInput) {
      saveDraftIfChanged();
    }
  };

  const addRefToMandatorySet = (div: HTMLDivElement, key: string) => {
    if (modalVisible) {
      const copy = { ...mandatoryFieldRefs.current };
      if (div) {
        copy[key] = div;
      } else {
        delete copy[key]; // need to delete references that no longer exist when switching progress checks
      }
      mandatoryFieldRefs.current = copy;
    }
  };

  const getLearningOutcomesForEvidencePart = (): LearningObjective[] => {
    if (program && ProgramUtils.getLearningOutcomesFromRole(program?.ID)) {
      let userRoles: string[] = [];
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      return ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);
    }

    if (progressCheckCompetence) {
      return progressCheckCompetence["Learning Objective"] ?? [];
    }

    if (programLearningObjectives.length > 0) {
      return programLearningObjectives;
    }

    return [];
  };

  const addPreviousIncidentDetails = () => {
    const updatedEvidenceJSON = addPreviousIncidentDetailsToEvidence({ evidenceParts, evidenceJSON, programSkills });
    setEvidenceJSON(updatedEvidenceJSON);
    setReuseIncidentModalVisible(false);
  };

  return (
    <IonModal
      id="evidenceModal"
      isOpen={modalVisible}
      onDidDismiss={() => resetModalState()}
      backdropDismiss={true}
      canDismiss={canDismiss}
      className={`fullscreen ${disableFocusTrap() ? "ion-disable-focus-trap" : ""}`}
    >
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          {confirmationEvidence ? (
            <IonButtons slot="start">
              <IonButton
                mode="ios"
                className="cancelButton"
                onClick={() => dismissModal()}
              >
                {"Close"}
              </IonButton>
            </IonButtons>
          ) : isNewEvidence ? (
            <>
              <IonButtons slot="start">
                <IonButton
                  mode="ios"
                  className="cancelButton"
                  onClick={() => newEvidenceCloseClicked()}
                >
                  {"Close"}
                </IonButton>
              </IonButtons>
              {!confirmationEvidence && <HeaderTitle>{"New Evidence"}</HeaderTitle>}
              {!confirmationEvidence && canSaveDraft && (
                <IonButtons slot="end">
                  {!savingDraft ? (
                    <IonButton
                      mode="ios"
                      className="cancelButton"
                      onClick={() => saveDraftClicked()}
                    >
                      <div className="font-semibold">{"Save draft"}</div>
                    </IonButton>
                  ) : (
                    <div className="flex items-center justify-center">
                      <IonSpinner className="h-[20px] w-[20px] text-white" />
                    </div>
                  )}
                </IonButtons>
              )}
            </>
          ) : isDraft ? (
            <>
              <IonButtons slot="start">
                <IonButton
                  mode="ios"
                  className="cancelButton"
                  onClick={() => draftCloseClicked()}
                >
                  {"Close"}
                </IonButton>
              </IonButtons>
              {!confirmationEvidence && <HeaderTitle>{"Draft"}</HeaderTitle>}
              {!confirmationEvidence && (
                <IonButtons slot="end">
                  <IonButton
                    mode="ios"
                    className="cancelButton"
                    onClick={() => deleteClicked()}
                  >
                    {"Delete"}
                  </IonButton>
                </IonButtons>
              )}
            </>
          ) : (
            <>
              <IonButtons slot="start">
                <IonButton
                  mode="ios"
                  className="cancelButton"
                  onClick={() => cancelClicked()}
                >
                  {"Close"}
                </IonButton>
              </IonButtons>
              {!confirmationEvidence && <HeaderTitle>{"Edit Evidence"}</HeaderTitle>}
              {!confirmationEvidence && (
                <IonButtons slot="end">
                  <IonButton
                    mode="ios"
                    className="cancelButton"
                    onClick={() => deleteClicked()}
                  >
                    {"Delete"}
                  </IonButton>
                </IonButtons>
              )}
            </>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent className="relative">
        {confirmationEvidence && (
          <UOCSubmitConfirmation
            evidence={confirmationEvidence}
            expiryDays={program?.EvidenceExpiryDays}
            onDone={() => { setConfirmationEvidence(null); dismissModal(); }}
          />
        )}
        {!confirmationEvidence && (
          <>
            <div className="sticky top-0 z-50 flex flex-col">
          {showErrorBanner && (
            <div
              ref={errorBannerRef}
              className="flex justify-center bg-cta-red py-4 text-acc-13px font-semibold text-white"
            >
              <div className="ml-safe-area-l mr-safe-area-r flex w-full max-w-app-column items-center justify-between">
                <div className="flex-1">
                  <div>{StringUtils.getErrorBannerHeaderFieldsText(incompleteMandatoryFields.length)}</div>
                  <div>{StringUtils.getErrorBannerText(incompleteMandatoryFields.length)}</div>
                </div>
                <div className="flex items-center">
                  <div className="mr-3">{`${activeMandatoryFieldIndex + 1}-${incompleteMandatoryFields.length}`}</div>
                  <div className="flex items-center">
                    <button
                      className="p-1"
                      onClick={() => {
                        if (!disablePrevChevron) {
                          setActiveMandatoryFieldIndex(activeMandatoryFieldIndex - 1);
                          scrollToMandatoryField(activeMandatoryFieldIndex - 1, incompleteMandatoryFields);
                        }
                      }}
                    >
                      <img src={disablePrevChevron ? Chevron_Prev_Inactive : Chevron_Prev_Active} />
                    </button>
                    <button
                      className="p-1"
                      onClick={() => {
                        if (!disableNextChevron) {
                          setActiveMandatoryFieldIndex(activeMandatoryFieldIndex + 1);
                          scrollToMandatoryField(activeMandatoryFieldIndex + 1, incompleteMandatoryFields);
                        }
                      }}
                    >
                      <img src={disableNextChevron ? Chevron_Next_Inactive : Chevron_Next_Active} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <ContentContainer
          style={{
            paddingBottom: keyboardActive && disableFocusTrap() ? keyboardHeight : 12,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AIAssistModal
            visible={aiAssistModalVisible}
            closeModal={() => setAiAssistModalVisible(false)}
            onEvidencePreviewSaved={(params) => processEvidencePreviewSaved(params)}
            initialEvidencePreview={latestEvidencePreview}
          />
          <SaveDraftModal
            visible={saveDraftModalVisible}
            cancelPressed={() => setSaveDraftModalVisible(false)}
            deletePressed={() => discardDraft()}
            savePressed={() => saveDraftAndClose()}
          />
          <DeleteEvidenceModal
            visible={deleteEvidenceModalVisible}
            isDraft={isDraft}
            cancelPressed={() => setDeleteEvidenceModalVisible(false)}
            deletePressed={() => deleteEvidenceAndClose()}
          />
          <EmptyTagModal
            visible={emptyTagModalVisible}
            dismissPressed={() => setEmptyTagModalVisible(false)}
          />
          <ExistingTagModal
            visible={existingTagModalVisible}
            dismissPressed={() => setExistingTagModalVisible(false)}
          />
          <UnsavedChangesModal
            visible={unsavedChangesModalVisible}
            cancelPressed={() => setUnsavedChangesModalVisible(false)}
            discardPressed={() => dismissModal()}
            savePressed={() => saveEvidenceAndClose()}
          />
          <ReuseIncidentModal
            visible={reuseIncidentModalVisible}
            reusePressed={() => addPreviousIncidentDetails()}
            cancelPressed={() => setReuseIncidentModalVisible(false)}
          />
          {program ? (
            <>
              <IonModal
                id="changeProgressCheckModal"
                isOpen={changeProgressCheckModal}
                backdropDismiss={true}
                canDismiss={true}
                className="fullscreen"
                onDidDismiss={() => setChangeProgressCheckModalVisible(false)}
              >
                <IonContent className="changeTaskingTypeModal">
                  <div className="changeTaskingTypeModalContainer">
                    <div>
                      <div className="changeTaskingTypeModalTitle">{"Are you sure you want to change the type?"}</div>
                      <div className="changeTaskingTypeModalText mx-[8px]">
                        {"Changing type will lose any information added for that type"}
                      </div>
                    </div>
                    <div>
                      <div className="tipBottomButtonContainer">
                        <button
                          className="tipBottomButton"
                          onClick={() => confirmChangeProgressCheck(tempProgressCheck)}
                        >
                          {"Change type"}
                        </button>
                      </div>
                      <div className="tipBottomButtonContainer">
                        <button
                          className="tipBottomButton"
                          onClick={() => setChangeProgressCheckModalVisible(false)}
                        >
                          {"Keep the current type"}
                        </button>
                      </div>
                    </div>
                  </div>
                </IonContent>
              </IonModal>
              <IonModal
                id="changeProgressCheckModal"
                isOpen={changeProgressCheckCompetenceModal}
                backdropDismiss={true}
                canDismiss={true}
                className="fullscreen"
                onDidDismiss={() => setChangeProgressCheckCompetenceModalVisible(false)}
              >
                <IonContent className="changeTaskingTypeModal">
                  <div className="changeTaskingTypeModalContainer">
                    <div>
                      <div className="changeTaskingTypeModalTitle">{"Are you sure you want to change the type?"}</div>
                      <div className="changeTaskingTypeModalText mx-[8px]">
                        {"Changing type will lose any information added for that type"}
                      </div>
                    </div>
                    <div>
                      <div className="tipBottomButtonContainer">
                        <button
                          className="tipBottomButton"
                          onClick={() => confirmChangeProgressCheckCompetence(tempProgressCheckCompetence)}
                        >
                          {"Change type"}
                        </button>
                      </div>
                      <div className="tipBottomButtonContainer">
                        <button
                          className="tipBottomButton"
                          onClick={() => setChangeProgressCheckCompetenceModalVisible(false)}
                        >
                          {"Keep the current type"}
                        </button>
                      </div>
                    </div>
                  </div>
                </IonContent>
              </IonModal>
              <IonModal
                id="changeTaskingTypeModal"
                isOpen={changeTaskingTypeModalVisible}
                backdropDismiss={true}
                canDismiss={true}
                className="fullscreen"
                onDidDismiss={() => setChangeTaskingTypeModalVisible(false)}
              >
                <IonContent className="changeTaskingTypeModal">
                  <div className="changeTaskingTypeModalContainer">
                    <div>
                      <div className="changeTaskingTypeModalTitle">
                        {"Are you sure you want to change the task type?"}
                      </div>
                      <div className="changeTaskingTypeModalText">
                        {"Changing task type will lose any information added for that task"}
                      </div>
                    </div>
                    <div>
                      <div className="tipBottomButtonContainer">
                        <button
                          className="tipBottomButton"
                          onClick={() => confirmChangeTaskType(tempTaskingType)}
                        >
                          {"Change task type"}
                        </button>
                      </div>
                      <div className="tipBottomButtonContainer">
                        <button
                          className="tipBottomButton"
                          onClick={() => setChangeTaskingTypeModalVisible(false)}
                        >
                          {"Keep the current task type"}
                        </button>
                      </div>
                    </div>
                  </div>
                </IonContent>
              </IonModal>
              <EvidenceHeader
                programName={program.Title}
                competenceName={competence?.Name}
              />
              {progressChecks?.length > 0 && (
                <EvidenceContentCard title="Progress check">
                  <div
                    className={classNames("reflectionActivityTypeContainer", {
                      "cursor-default": disableProgressCheckDropdown,
                    })}
                  >
                    <IonList
                      mode="md"
                      className="reflectionActivityList"
                      color="white"
                    >
                      <IonItem
                        mode="md"
                        className="reflectionActivityItem"
                        color="white"
                      >
                        <IonSelect
                          color="white"
                          mode="md"
                          className={classNames("reflectionActivitySelectItem w-full", {
                            "hide-select-arrow": disableProgressCheckDropdown,
                          })}
                          id="evidence-modal-progress-check-select"
                          interface="popover"
                          justify="space-between"
                          interfaceOptions={{
                            size: "cover",
                            alignment: "start",
                            cssClass: "evidence-select-popover",
                          }}
                          disabled={disableProgressCheckDropdown}
                          placeholder="Select progress check"
                          value={progressCheck?.Name || undefined}
                          onClick={updatePopoverTriggerWidth}
                          onIonChange={progressCheckChanged}
                        >
                          {progressChecks.map((item: ProgressCheck) => (
                            <IonSelectOption
                              color="white"
                              key={item.Name}
                              value={item.Name}
                            >
                              {item.Name}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                    </IonList>
                  </div>
                </EvidenceContentCard>
              )}
              {progressCheckCompetences?.length > 0 && (
                <EvidenceContentCard title={"Evidence type"}>
                  <div
                    className={classNames("reflectionActivityTypeContainer", {
                      "cursor-default": disableProgressCheckDropdown,
                    })}
                  >
                    <IonList
                      mode="md"
                      className="reflectionActivityList"
                      color="white"
                    >
                      <IonItem
                        className="reflectionActivityItem"
                        color="white"
                        mode="md"
                      >
                        <IonSelect
                          aria-label={StringUtils.getCompetenceCardTitle(program.ID)}
                          color="white"
                          mode="md"
                          className={classNames("reflectionActivitySelectItem", {
                            "hide-select-arrow": disableProgressCheckCompetenceDropdown,
                          })}
                          id="evidence-modal-progress-check-competence-select"
                          interface="popover"
                          justify="space-between"
                          interfaceOptions={{
                            size: "cover",
                            alignment: "start",
                            cssClass: "evidence-select-popover",
                          }}
                          disabled={disableProgressCheckCompetenceDropdown}
                          placeholder="Select evidence"
                          value={progressCheckCompetence?.Name || undefined}
                          onClick={updatePopoverTriggerWidth}
                          onIonChange={progressCheckCompetenceChanged}
                        >
                          {progressCheckCompetences.map((item: CompetenceActivityClass) => {
                            if (item.hideCompetence) {
                              if (item.Name !== progressCheckCompetence?.Name) {
                                return null;
                              }
                            }

                            return (
                              <IonSelectOption
                                color="white"
                                key={item.Name}
                                value={item.Name}
                              >
                                {item.Name}
                              </IonSelectOption>
                            );
                          })}
                        </IonSelect>
                      </IonItem>
                    </IonList>
                  </div>
                </EvidenceContentCard>
              )}
              {competences?.length > 0 && !ProgramUtils.isTelephoneTriageModule(program.ID) && (
                <EvidenceContentCard title="Enhanced Skill undertaken">
                  <div className="reflectionActivityTypeContainer">
                    <IonList
                      mode="md"
                      className="reflectionActivityList"
                      color="white"
                    >
                      <IonItem
                        mode="md"
                        className="reflectionActivityItem"
                        color="white"
                      >
                        <IonSelect
                          color="white"
                          mode="md"
                          className="reflectionActivitySelectItem"
                          interface="popover"
                          justify="space-between"
                          interfaceOptions={{
                            size: "cover",
                            alignment: "start",
                            cssClass: "evidence-select-popover",
                          }}
                          placeholder="Select enhanced skill"
                          value={competence?.Name || undefined}
                          onIonChange={enhancedSkillChanged}
                          onClick={updatePopoverTriggerWidth}
                        >
                          {competences.map((item: CompetenceActivityClass) => (
                            <IonSelectOption
                              color="white"
                              key={item.Name}
                              value={item.Name}
                            >
                              {item.Name}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                    </IonList>
                  </div>
                </EvidenceContentCard>
              )}
              {evidenceParts?.map((evidencePart: EvidencePart, index) => {
                const isSiblingPart =
                  evidencePart["Linked Part type"] && evidencePart["Linked Part type"] === "Sibling";
                const isFilledInBySupervisor = evidencePart.FilledInBySupervisor;

                if (isFilledInBySupervisor || isSiblingPart) {
                  return null;
                }

                return (
                  <EvidencePartWrapper
                    key={evidencePart.ID}
                    isErrorBannerVisible={showErrorBanner}
                    handleElementReference={(ref) => addRefToMandatorySet(ref, evidencePart.Name)}
                    isMandatory={evidencePart.options === "Mandatory" || evidencePart["option type"] === "Mandatory"}
                    isPartIncomplete={incompleteMandatoryFields.includes(evidencePart.Name)}
                    wrapperControlsBottomMargin={EvidenceUtils.evidencePartHasNoContainer(evidencePart)}
                  >
                    <ProgramModuleEvidencePart
                      evidencePart={evidencePart}
                      blurRichText={() => blurRichText()}
                      focusRichText={() => setRichTextBoxFocused(true)}
                      evidenceJSON={evidenceJSON}
                      index={index}
                      onInputBlurred={() => saveDraftIfChanged()}
                      onValueChanged={(value: any, isInput?: boolean) =>
                        evidencePartChanged(evidencePart, value, isInput)
                      }
                      onChildValueChanged={(part: EvidencePart, value: any, isInput?: boolean) =>
                        evidencePartChanged(part, value, isInput)
                      }
                      key={evidencePart.ID}
                      program={program}
                      evidenceDefinitionID={programEvidenceDefinition?.ID}
                      progressCheckCompetence={progressCheckCompetence}
                      mandatoryStandards={programMandatoryStandards}
                      optionalStandards={programOptionalStandards}
                      skills={programSkills}
                      preselectedSkill={preselectedSkill}
                      competence={competence}
                      setExistingTagModalVisible={() => setExistingTagModalVisible(true)}
                      setEmptyTagModalVisible={() => setEmptyTagModalVisible(true)}
                      learningOutcomes={getLearningOutcomesForEvidencePart()}
                      preselectedLearningObjective={preselectedLearningObjective}
                      expandStandardId={expandStandardId}
                      currentProgressCheckId={progressCheck?.ID ?? ""}
                      evidencePartWrapperProps={{
                        isErrorBannerVisible: showErrorBanner,
                        mandatoryFields: incompleteMandatoryFields,
                        addRefToMandatorySet: addRefToMandatorySet,
                      }}
                    />
                  </EvidencePartWrapper>
                );
              })}
            </>
          ) : (
            <>
              <EvidenceHeader programName="Personal Evidence" />
              <PersonalEvidenceDisclaimer />
              <EvidencePartWrapper
                isErrorBannerVisible={showErrorBanner}
                handleElementReference={(ref) => addRefToMandatorySet(ref, "Title")}
                isMandatory={true}
                isPartIncomplete={incompleteMandatoryFields.includes("Title")}
              >
                <EvidenceContentCard
                  title="Title"
                  mandatory={true}
                >
                  <input
                    ref={titleRef}
                    className="reflectionTitleInput"
                    placeholder="Give your evidence a title..."
                    autoComplete="off"
                    spellCheck
                    autoCapitalize="words"
                    id="reflectionTitleInput"
                    value={title}
                    onFocus={titleFocused}
                    onChange={titleChanged}
                    onBlur={titleBlurred}
                  />
                </EvidenceContentCard>
              </EvidencePartWrapper>
              <EvidenceContentCard title="Activity description">
                <div className="ml-[-2.5px] overflow-hidden bg-white">
                  <Textarea
                    placeholder="Provide a brief description..."
                    id="reflection-activity-description-input"
                    spellCheck
                    value={activityDescription}
                    onChange={activityDescriptionChanged}
                    onFocus={activityDescriptionFocused}
                    onBlur={activityDescriptionBlurred}
                    variant="plain"
                    slotProps={{
                      textarea: {
                        ref: activityDescriptionRef,
                      },
                    }}
                    sx={{
                      "--Textarea-placeholderOpacity": 1,
                      backgroundColor: "white",
                      minHeight: "28px",
                      color: activityDescription.length ? "black" : "var(--grey-60)", // different color for placeholder text
                      padding: 0,
                      fontSize: "20px",
                      fontWeight: "600",
                      "@media (max-width: 500px)": {
                        fontSize: "17px",
                      },
                      ":hover": {
                        color: activityDescription.length ? "black" : "var(--grey-60)", // different color for placeholder text
                      },
                    }}
                  />
                </div>
              </EvidenceContentCard>
              <EvidencePartWrapper
                isErrorBannerVisible={showErrorBanner}
                handleElementReference={(ref) => addRefToMandatorySet(ref, "Date of reflection")}
                isMandatory={true}
                isPartIncomplete={incompleteMandatoryFields.includes("Date of reflection")}
              >
                <EvidenceContentCard
                  title="Date of reflection"
                  mandatory={true}
                >
                  <div
                    id="reflectionDatePicker"
                    className="reflectionDateContainer"
                    onClick={() => openDatePicker()}
                  >
                    <div className="reflectionDateText">{format(date, "dd / MM / yyyy")}</div>
                    <img
                      src={Button_Dropdown}
                      className="reflectionsHeaderDropdown"
                      alt=""
                    />
                    <IonPopover
                      trigger="reflectionDatePicker"
                      id="date-range-picker"
                      isOpen={datePickerVisible}
                      arrow
                      onDidDismiss={() => {
                        setDatePickerVisible(false);
                        saveDraftIfChanged();
                      }}
                    >
                      <div className="p-5 pt-2">
                        <DayPicker
                          mode={"single"}
                          weekStartsOn={1}
                          selected={date}
                          defaultMonth={date}
                          onSelect={(data: any) => {
                            data && setDate(data);
                          }}
                          endMonth={new Date()}
                          disabled={{ after: new Date() }}
                          classNames={{
                            selected: `bg-cta-blue text-white rounded-[10px]`,
                            today: isToday(date) ? "text-white" : getDefaultClassNames().today,
                            month_caption: `${defaultClassNames.month_caption} text-[#595959] font-normal`,
                          }}
                          components={{
                            Weekday: (el) => <WeekDayRenderer element={el} />,
                          }}
                        />
                      </div>
                    </IonPopover>
                  </div>
                </EvidenceContentCard>
              </EvidencePartWrapper>
              <EvidencePartWrapper
                isErrorBannerVisible={showErrorBanner}
                handleElementReference={(ref) => addRefToMandatorySet(ref, "Activity")}
                isMandatory={true}
                isPartIncomplete={incompleteMandatoryFields.includes("Activity")}
              >
                <EvidenceContentCard
                  title="CPD activity"
                  mandatory={true}
                >
                  <div
                    className="reflectionActivityTypeContainer w-full"
                    onClick={() => {
                      changeActivityState();
                    }}
                  >
                    <IonList
                      mode="md"
                      className="reflectionActivityList w-full"
                      color="white"
                    >
                      <IonItem
                        mode="md"
                        className="reflectionActivityItem"
                        color="white"
                      >
                        <IonSelect
                          color="white"
                          mode="md"
                          className="reflectionActivitySelectItem w-full"
                          interface="popover"
                          justify="space-between"
                          interfaceOptions={{
                            size: "cover",
                            alignment: "start",
                            cssClass: "evidence-select-popover",
                          }}
                          placeholder="What type of CPD activity are you reflecting on"
                          value={activity?.type || undefined}
                          onIonChange={activityChanged}
                          onClick={updatePopoverTriggerWidth}
                        >
                          {activityTypes.map((item) => {
                            return (
                              <IonSelectOption
                                color="white"
                                key={item.type}
                                value={item.type}
                              >
                                {item.type}
                              </IonSelectOption>
                            );
                          })}
                        </IonSelect>
                      </IonItem>
                    </IonList>
                  </div>
                </EvidenceContentCard>
              </EvidencePartWrapper>
              <EvidencePartWrapper
                isErrorBannerVisible={showErrorBanner}
                handleElementReference={(ref) => addRefToMandatorySet(ref, "Date of activity")}
                isMandatory={true}
                isPartIncomplete={incompleteMandatoryFields.includes("Date of activity")}
              >
                <EvidenceContentCard
                  title="Date of activity"
                  mandatory={true}
                >
                  <div
                    id="activityDatePicker"
                    className="reflectionDateContainer max-w-date-rage-picker"
                    onClick={() => openActivityDatePicker()}
                  >
                    {activityDateMode === "single" ? (
                      <div className="reflectionDateText">{format(activitySingleDate, "dd / MM / yyyy")}</div>
                    ) : (
                      <div className="reflectionDateText flex">
                        <div>
                          {activityDateRange ? format(activityDateRange.from, "dd / MM / yyyy") : "DD / MM / YYYY"}
                        </div>
                        {activityDateRange && activityDateRange.to && (
                          <div className="whitespace-pre-wrap">{" - "}</div>
                        )}
                        <div>
                          {activityDateRange && activityDateRange.to
                            ? format(activityDateRange.to, "dd / MM / yyyy")
                            : ""}
                        </div>
                      </div>
                    )}
                    <img
                      src={Button_Dropdown}
                      className="reflectionsHeaderDropdown"
                      alt=""
                    />
                    <IonPopover
                      trigger="activityDatePicker"
                      id="activity-date-range-picker"
                      isOpen={activityDatePickerVisible}
                      onDidDismiss={() => {
                        setActivityDatePickerVisible(false);
                        saveDraftIfChanged();
                      }}
                    >
                      <div className="rounded-xl p-5">
                        <div className="mb-3 flex gap-2">
                          <button
                            className={`flex-1 rounded-md border border-solid py-2 ${activityDateMode === "single" ? "border-cta-blue bg-cta-blue text-white" : "border-[#D5D3D5] bg-transparent text-[#BEBEBE]"}`}
                            onClick={() => handleDateRangeModeChange("single")}
                          >
                            {"Single Date"}
                          </button>
                          <button
                            className={`flex-1 rounded-md border border-solid py-2 ${activityDateMode === "range" ? "border-cta-blue bg-cta-blue text-white" : "border-[#D5D3D5] bg-transparent text-[#BEBEBE]"}`}
                            onClick={() => handleDateRangeModeChange("range")}
                          >
                            {"Date Range"}
                          </button>
                        </div>
                        {activityDateMode === "single" ? (
                          <DayPicker
                            mode={"single"}
                            weekStartsOn={1}
                            selected={activitySingleDate}
                            defaultMonth={activitySingleDate}
                            onSelect={(data: any) => {
                              data && setActivitySingleDate(data);
                            }}
                            endMonth={new Date()}
                            disabled={{ after: new Date() }}
                            classNames={{
                              selected: `bg-cta-blue text-white rounded-[10px]`,
                              today: isToday(activitySingleDate) ? "text-white" : getDefaultClassNames().today,
                              month_caption: `${defaultClassNames.month_caption} text-[#595959] font-normal`,
                            }}
                            components={{
                              Weekday: (el) => <WeekDayRenderer element={el} />,
                            }}
                          />
                        ) : (
                          <DayPicker
                            mode={"range"}
                            selected={activityDateRange}
                            defaultMonth={activityDateRange?.from}
                            weekStartsOn={1}
                            onSelect={(_: any, triggerDate: Date) => handleDateRangeChange(triggerDate)}
                            endMonth={new Date()}
                            disabled={{ after: new Date() }}
                            classNames={{
                              selected:
                                activityDateRange?.from && !activityDateRange.to
                                  ? `bg-cta-blue text-white rounded-[10px]`
                                  : "",
                              today:
                                activityDateRange?.from && isToday(activityDateRange?.from)
                                  ? "text-white"
                                  : getDefaultClassNames().today,
                              month_caption: `${defaultClassNames.month_caption} text-[#595959] font-normal`,
                            }}
                            components={{
                              Weekday: (el) => <WeekDayRenderer element={el} />,
                            }}
                          />
                        )}
                      </div>
                    </IonPopover>
                  </div>
                </EvidenceContentCard>
              </EvidencePartWrapper>
              <EvidenceContentCard title="Activity duration">
                <DurationPicker
                  hours={numberOfHours}
                  onChange={onDurationPickerChange}
                />
              </EvidenceContentCard>
              <EvidenceContentCard title="Custom tags">
                {customTags.length > 0 && (
                  <div className={`relative mb-2 flex flex-row flex-wrap items-center gap-2`}>
                    {customTags.map((tag) => {
                      return (
                        <div
                          className="reflectionTagContainer"
                          key={tag.tag}
                          onClick={() => deleteCustomTag(tag.tag)}
                        >
                          <div className="reflectionTagText">{tag.tag}</div>
                          <img
                            src={Icon_Attachment_Delete}
                            className="attachmentIconClose"
                            alt=""
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                <CustomTagInput
                  oldTags={getOldTags()}
                  handleAddTag={(newTag) => saveNewTag(newTag)}
                  handleFocus={() => changeActivityState()}
                />
              </EvidenceContentCard>
              <EvidenceContentCard title="Relevant documents (85MB limit / file)">
                <AttachmentInput
                  attachments={attachments}
                  viewingAttachment={viewingAttachment}
                  viewingAttachmentFilename={viewingAttachmentFilename}
                  viewAttachment={(attachment) => viewAttachment(attachment)}
                  deleteAttachment={(filename) => deleteAttachment(filename)}
                  openFileSelector={() => {
                    openFileSelector();
                    changeActivityState();
                  }}
                />
              </EvidenceContentCard>
              <EvidencePartWrapper
                isErrorBannerVisible={showErrorBanner}
                handleElementReference={(ref) => addRefToMandatorySet(ref, "Reflections")}
                isMandatory={true}
                isPartIncomplete={incompleteMandatoryFields.includes("Reflections")}
              >
                <EvidenceContentCard
                  title="Reflection"
                  mandatory={true}
                >
                  <Editor
                    init={EditorUtils.ReflectionsEditorConfig}
                    tinymceScriptSrc={process.env.PUBLIC_URL + "/tinymce/tinymce.min.js"}
                    id="reflectionsEditorRef"
                    onInit={(event: any, editor: any) => (reflectionsEditorRef.current = editor)}
                    value={reflections}
                    onEditorChange={(a: any, editor: any) => {
                      a.length > 0 && editor.getBody().setAttribute("data-mce-placeholder", "");
                      setReflection(a);
                      isNewEvidence && setCanSaveDraft(true);
                    }}
                    onFocus={reflectionsEditorFocused}
                    onBlur={reflectionsEditorBlurred}
                    licenseKey="gpl"
                  />
                </EvidenceContentCard>
              </EvidencePartWrapper>
              <EvidencePartWrapper
                isErrorBannerVisible={showErrorBanner}
                handleElementReference={(ref) => addRefToMandatorySet(ref, "Benefits")}
                isMandatory={true}
                isPartIncomplete={incompleteMandatoryFields.includes("Benefits")}
              >
                <EvidenceContentCard title="Benefits">
                  <Editor
                    init={EditorUtils.BenefitsEditorConfig}
                    tinymceScriptSrc={process.env.PUBLIC_URL + "/tinymce/tinymce.min.js"}
                    id="benefitsEditorRef"
                    onInit={(event: any, editor: any) => (benefitsEditorRef.current = editor)}
                    value={benefits}
                    onEditorChange={(a: any, editor: any) => {
                      a.length > 0 && editor.getBody().setAttribute("data-mce-placeholder", "");
                      setBenefits(a);
                      isNewEvidence && setCanSaveDraft(true);
                    }}
                    onFocus={benefitsEditorFocused}
                    onBlur={benefitsEditorBlurred}
                    licenseKey="gpl"
                  />
                </EvidenceContentCard>
              </EvidencePartWrapper>
              <EvidenceContentCard title="Learning objectives">
                <Editor
                  init={EditorUtils.ObjectivesEditorConfig}
                  tinymceScriptSrc={process.env.PUBLIC_URL + "/tinymce/tinymce.min.js"}
                  id="objectivesEditorRef"
                  onInit={(event: any, editor: any) => (objectivesEditorRef.current = editor)}
                  value={objectives}
                  onEditorChange={(a: any, editor: any) => {
                    a.length > 0 && editor.getBody().setAttribute("data-mce-placeholder", "");
                    setObjectives(a);
                    isNewEvidence && setCanSaveDraft(true);
                  }}
                  onFocus={objectivesEditorFocused}
                  onBlur={objectivesEditorBlurred}
                  licenseKey="gpl"
                />
              </EvidenceContentCard>
            </>
          )}
          <AttachmentProgressIndicator visible={handlingAttachments} />
          <SaveEvidenceButton
            disabled={isSaveButtonDisabled()}
            saving={saving}
            isDraft={isDraft}
            isNewEvidence={isNewEvidence}
            buttonText={EvidenceUtils.getSaveEvidenceButtonText(programEvidenceDefinition ?? undefined)}
            saveEvidence={() => {
              changeActivityState();
              const mandatoryFieldsCheck = areAllMandatoryFieldsComplete();
              if (mandatoryFieldsCheck.complete) {
                saveEvidence();
              } else {
                setShowErrorBanner(true);
                setIncompleteMandatoryFields([...mandatoryFieldsCheck.incompleteFields]);
                setActiveMandatoryFieldIndex(0);
                scrollToMandatoryField(0, mandatoryFieldsCheck.incompleteFields);
              }
            }}
          />
        </ContentContainer>
        {isAIDictationAvailable && !program && (
          <div className="sticky bottom-6 z-50 mx-auto flex max-w-app-column flex-col justify-end gap-4 pl-safe-area-l pr-safe-area-r">
            <AIAssistButton
              buttonPressed={async () => {
                await FirebaseService.logEvent("ai_assist_button_pressed");
                setAiAssistSessionId(null);
                setAiAssistModalVisible(true);
              }}
            />
            {aiAssistSessionId && (
              <AIAssistFeedback
                visible={aiAssistFeedbackVisible}
                sessionId={aiAssistSessionId}
                messages={aiAssistMessages}
                onSubmitFeedback={async () => {
                  await FirebaseService.logEvent("ai_assist_feedback_submitted");
                  setAiAssistFeedbackVisible(false);
                  setAiAssistSessionId(null);
                  setAiAssistMessages([]);
                }}
              />
            )}
          </div>
        )}
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default EvidenceModal;
